/* eslint-disable no-param-reassign */
/* eslint-disable no-multi-assign */

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable complexity */

import cloneDeepWith from 'lodash/cloneDeepWith';
import isNil from 'lodash/isNil';
import unset from 'lodash/unset';
import {
    AutoIncrementIDGenerator,
    RandomIDGenerator,
    RandomIncrementIDGenerator,
    getHashStringWith64Bytes,
} from '@tencent/slide-sdk-model/src/utils/id-generator';
import {
    K_DEFAULT_TRIGGER_ID,
    PATH_TIMING,
    DataType,
    MEDIA_TRIGGER_ID_PREFIX,
    UnitTimeNode,
    generateUpperUUID,
    shallowOmit,
} from '@tencent/slide-sdk-model';
import ThemeData from '@tencent/slide-sdk-model/src/template/theme-template.json';
import merge from 'lodash/merge';
import set from 'lodash/set';

import {
    STAxPos,
    STBarDir,
    STBarGrouping,
    STColorSchemeIndex,
    STCompoundLine,
    STCrossBetween,
    STCrosses,
    STDispBlanksAs,
    STDLblPos,
    STErrBarType,
    STErrDir,
    STErrValType,
    STFontCollectionIndex,
    STHyperlinkColor,
    STLayoutMode,
    STLayoutTarget,
    STLblAlgn,
    STLegendPos,
    STLineCap,
    STLineEndLength,
    STLineEndType,
    STLineEndWidth,
    STMarkerStyleDmlC,
    STOnOffStyleType,
    STOrientationDmlC,
    STPathFillMode,
    STPathShadeType,
    STPlaceholderType,
    STPresetLineDashVal,
    STPresetPatternVal,
    STRectAlignment,
    STSchemeColorVal,
    STShape,
    STShapeType,
    STSlideSizeType,
    STTextAlignType,
    STTextAnchoringType,
    STTextAutonumberScheme,
    STTextStrikeType,
    STTextTabAlignType,
    STTextUnderlineType,
    STTextVerticalType,
    STTextWrappingType,
    STTickLblPos,
    STTickMark,
    STTileFlipMode,
    STTimeUnit,
    STTLAnimateBehaviorCalcMode,
    STTLAnimateBehaviorValueType,
    STTLAnimateColorDirection,
    STTLAnimateColorSpace,
    STTLAnimateEffectTransition,
    STTLAnimateMotionBehaviorOrigin,
    STTLAnimateMotionPathEditMode,
    STTLBehaviorAccumulateType,
    STTLBehaviorAdditiveType,
    STTLBehaviorOverrideType,
    STTLBehaviorTransformType,
    STTLCommandType,
    STTLTimeNodeFillType,
    STTLTimeNodeMasterRelation,
    STTLTimeNodePresetClassType,
    STTLTimeNodeRestartType,
    STTLTimeNodeSyncType,
    STTLTimeNodeType,
    STTLTriggerEvent,
    STTLTriggerRuntimeNode,
    STTrendlineType,
    STGrouping,
    SubChartType,
    STRadarStyle,
    STScatterStyle,
    STOfPieType,
    STSplitType,
    STSizeRepresents,
    STBuiltInUnit,
    STAttachmentClassify,
    STAttachmentShowType,
    STAddonMode,
    STDirectionPml,
    STPresetCameraType,
    STLightRigType,
    STLightRigDirection,
    STBevelPresetType,
    STPresetMaterialType,
} from '@tencent/slide-sdk-pb-model';
import { IMAGE_MIME_TYPE } from '@tencent/slide-sdk-model/src/constants/media-constants';
import { prstGeomGdNames } from './constant';
import './smart-enum';
import type { ICTBooleanDmlC, ICTConnectionDmlA } from '@tencent/slide-sdk-pb-model';

export function migratePresentation(
    presentation,
    { sessionId = '', useRandomId }: { sessionId?: string; useRandomId?: boolean } = {},
) {
    return new SlideDataMigrator().migratePresentation(presentation, sessionId, useRandomId);
}

export class SlideDataMigrator {
    public idSep = '__';

    public ctx: {
        sessionId: string;
        idsMap: Record<string, string>;
        presentation?: any;
        page?: {
            id: string;
            type: string;
        };
        section?: {
            currentSlideIndex: number;
        };
        mediaIdTimingMap: Record<string, any>; // 用于存储从音视频到动画的迁移信息
        connections: ICTConnectionDmlA[]; // 用于存储连接线信息，id 在迁移结束时统一修正
        defaultFont: string; // 用于将默认字体填充入主题
        themeList: any[];
    };

    private idGenerator = new AutoIncrementIDGenerator();
    private useRandomId = false;

    public constructor() {
        this.ctx = {
            idsMap: {},
            sessionId: RandomIDGenerator.next(),
            mediaIdTimingMap: {},
            connections: [],
            defaultFont: '',
            themeList: [],
        };
    }

    public migratePresentation = (data, sessionId, useRandomId = false) => {
        this.useRandomId = useRandomId;
        this.ctx.presentation = data;
        this.ctx.sessionId = sessionId;
        this.ctx.defaultFont = data.defaultTextStyleList?.[0]?.defaultRunProps?.font?.ea?.typeface ?? '';

        data.resource = {
            ...data.resource,
            imageResource: {
                ...data.resource?.imageResource,
                resourceMap: {
                    ...data.resource?.imageResource?.resourceMap,
                },
            },
            audioResource: {
                ...data.resource?.audioResource,
                resourceMap: {
                    ...data.resource?.audioResource?.resourceMap,
                },
            },
            videoResource: {
                ...data.resource?.videoResource,
                resourceMap: {
                    ...data.resource?.videoResource?.resourceMap,
                },
            },
        };

        data.slideList = (data.slideList ?? []).filter(Boolean);
        data.layoutList = (data.layoutList ?? []).filter(Boolean);
        data.masterList = (data.masterList ?? []).filter(Boolean);
        data.themeList = (data.themeList ?? []).filter(Boolean);
        data.sectionList = data.sectionList ?? [];

        const newData = {
            version: '3.0',
            type: DataType.Presentation as const,
            id: this.getId('root', 'presentation') as string,
            firstSlideNum: data.firstSlideNum,
            slideList: this.migrateSlideList(data.slideList),
            layoutMap: this.migrateLayoutList(data.layoutList),
            noteSlideMap: data.slideList.reduce((map, slide) => {
                const noteSlide = this.migrateNoteSlide(slide);
                if (noteSlide?.id) {
                    map[noteSlide.id] = noteSlide;
                }
                return map;
            }, {}),
            notesMasterMap:
                data.notesMasterList?.reduce((map, notesMaster) => {
                    const noteSlide = this.migratePage(notesMaster, DataType.NotesMaster);
                    if (noteSlide?.id) {
                        map[noteSlide.id] = noteSlide;
                    }
                    return map;
                }, {}) ?? {},
            masterList: this.migrateMasterList(data.masterList, data.layoutList),
            themeList: this.migrateThemeList(data.themeList),
            sectionMap: this.migrateSectionList(data.sectionList),
            resourceMap: this.migrateResourceMap(data.resource),
            sldSz: this.migrateSlideSize(data),
            notesSz: {
                cx: 6858000,
                cy: 9144000,
            },
            defaultTextStyle: this.migrateTextStyleList(data.defaultTextStyleList),
            tblStyleList: this.migrateTblStyleList(data.tblStyleList),
            showSpecialPlsOnTitleSld: data.showSpecialPlsOnTitleSld ?? Boolean(data.showSpecialPlsOnTitleSld),
            slideGuideList: this.migrateGuideList(data.guides?.slideGuides),
        };

        this.updateConnectionsId();
        this.resetLayoutId(newData);

        return newData;
    };

    public updateConnectionsId() {
        for (const connection of this.ctx.connections) {
            connection.id = this.getId(connection.id, 'shape', false);
        }
    }

    public migrateGuideList(guides) {
        if (!guides?.length) {
            return [];
        }

        return guides.map((guide, index) => this.migrateGuide(guide, index)).filter(Boolean);
    }

    public migrateGuide(guide, index) {
        if (!guide) return;

        const newGuide: any = {
            id: `${index + 1}`,
        };

        if (!isNil(guide.orient)) {
            newGuide.orient = STDirectionPml[guide.orient];
        }

        if (!isNil(guide.pos)) {
            newGuide.pos = guide.pos;
        }

        if (!isNil(guide.color)) {
            newGuide.clr = this.migrateColor({
                colorType: 'rgb',
                colorValue: guide.color,
            });
        }

        return newGuide;
    }

    public migrateSlideSize = (presentation) => {
        const ratio = presentation.slideWidth / presentation.slideHeight;
        let sizeType = 'custom';
        if (ratio === 4 / 3) {
            sizeType = 'screen4x3';
        } else if (ratio === 16 / 9) {
            sizeType = 'screen16x9';
        } else if (ratio === 16 / 10) {
            sizeType = 'screen16x10';
        }

        return {
            cx: presentation.slideWidth,
            cy: presentation.slideHeight,
            type: STSlideSizeType[sizeType],
        };
    };

    public migrateTblStyleList = (tblStyleList) => {
        if (isNil(tblStyleList)) {
            return;
        }
        const newTblStyleList: any = {};
        if (!isNil(tblStyleList.def)) {
            newTblStyleList.def = tblStyleList.def;
        }
        if (!isNil(tblStyleList.tableStyleEntryList)) {
            newTblStyleList.tblStyle = tblStyleList.tableStyleEntryList.map((i) => this.migrateTableStyle(i));
        }
        return newTblStyleList;
    };

    public migrateTableStyle = (tableStyle) => {
        const newTableStyle: any = {};
        if (!isNil(tableStyle.styleName)) {
            newTableStyle.styleName = tableStyle.styleName;
        }
        if (!isNil(tableStyle.styleId)) {
            newTableStyle.styleId = tableStyle.styleId;
        }
        ['wholeTbl', 'band1H', 'band2H', 'band1V', 'band2V', 'lastCol', 'firstCol', 'lastRow', 'firstRow'].forEach(
            (key) => {
                if (!isNil(tableStyle[key])) {
                    newTableStyle[key] = this.migrateTablePartStyle(tableStyle[key]);
                }
            },
        );
        return newTableStyle;
    };

    public migrateTablePartStyle = (tablePartStyle) => {
        const newTablePartStyle: any = {};
        if (!isNil(tablePartStyle.tcTxStyle)) {
            newTablePartStyle.tcTxStyle = this.migrateTableCellTextStyle(tablePartStyle.tcTxStyle);
        }
        if (!isNil(tablePartStyle.tcStyle)) {
            newTablePartStyle.tcStyle = this.migrateTableCellStyle(tablePartStyle.tcStyle);
        }
        return newTablePartStyle;
    };

    public migrateTableCellStyle = (tcStyle) => {
        const newTcStyle: any = {};
        if (!isNil(tcStyle.tcBdr)) {
            newTcStyle.tcBdr = this.migrateTableBorder(tcStyle.tcBdr);
        }
        if (!isNil(tcStyle.fill)) {
            newTcStyle.fill = this.migrateBackground(tcStyle.fill);
        }
        return newTcStyle;
    };

    public migrateTableBorder = (tcBdr) => {
        const newTcBdr: any = {};
        ['bottom', 'insideH', 'insideV', 'left', 'right', 'top', 'tl2br', 'tr2bl'].forEach((key) => {
            if (!isNil(tcBdr[key]?.ln)) {
                newTcBdr[key] = this.migrateOutline(tcBdr[key].ln);
            }
        });
        return newTcBdr;
    };

    public migrateTableCellTextStyle = (tableCellTextStyle) => {
        const newTableCellTextStyle: any = {};
        if (!isNil(tableCellTextStyle.b)) {
            newTableCellTextStyle.b = STOnOffStyleType[tableCellTextStyle.b];
        }
        if (!isNil(tableCellTextStyle.i)) {
            newTableCellTextStyle.i = STOnOffStyleType[tableCellTextStyle.i];
        }
        if (!isNil(tableCellTextStyle.color)) {
            Object.assign(newTableCellTextStyle, this.migrateColor(tableCellTextStyle.color));
        }
        if (!isNil(tableCellTextStyle.fontRef)) {
            newTableCellTextStyle.fontRef = this.migrateFontRef(tableCellTextStyle.fontRef);
        }
        return newTableCellTextStyle;
    };

    public migrateFontRef = (fontRef) => {
        const newFontRef: any = {};
        if (!isNil(fontRef.color)) {
            // 3.0里存在两个 color，初步看只用了一个，因此只转一个
            Object.assign(newFontRef, this.migrateColor(fontRef.color));
        }
        if (!isNil(fontRef.idx)) {
            newFontRef.idx = STFontCollectionIndex[fontRef.idx];
        }
        return newFontRef;
    };

    public migrateSlideList = (slideList) => {
        return slideList.filter(Boolean).map((slide) => this.migratePage(slide, DataType.Slide));
    };

    public migrateLayoutList = (layoutList) => {
        const map = {};
        layoutList.forEach((layout) => {
            if (!layout.masterId) {
                return;
            }
            map[this.getPageId(layout)] = this.migratePage(layout, DataType.SlideLayout);
        });
        return map;
    };

    public migrateMasterList = (masterList, layoutList) => {
        const newMasterList: any[] = [];
        for (const master of masterList) {
            const newMaster = this.migratePage(master, DataType.SlideMaster);
            newMaster.layoutIdList = layoutList
                .filter((layout) => this.getPageId({ id: layout.masterId, type: 'master' }) === newMaster.id)
                .map((layout) => this.getPageId(layout));
            newMasterList.push(newMaster);
        }

        return newMasterList;
    };

    public migrateThemeList = (themeList) => {
        // themeList 按 id 去重
        const themeMap = new Map<string, any>();
        for (const theme of themeList) {
            if (!themeMap.has(theme.id)) {
                themeMap.set(theme.id, theme);
            }
        }
        return Array.from(themeMap.values()).map(this.migrateTheme).concat(this.ctx.themeList);
    };

    public migrateSectionList = (sectionList) => {
        if (!sectionList?.length) return {};

        this.ctx.section = { currentSlideIndex: 0 };
        return Object.fromEntries(
            sectionList
                .filter(Boolean)
                .map(this.migrateSection)
                .map((section) => [section.id, section]),
        );
    };

    public migratePage = (slideData, type) => {
        this.ctx.page = slideData;

        const newSlide: any = {
            // slide 的 id 一定不能重复，且不会在前置迁移中使用，因此 slide 的 id 始终生成一个新的
            id: this.getPageId(slideData, type === DataType.Slide),
            type,
        };

        if (slideData.layoutId) {
            newSlide.layoutId = this.getPageId({ id: slideData.layoutId, type: 'layout' });
        }

        if (slideData.masterId) {
            newSlide.masterId = this.getPageId({ id: slideData.masterId, type: 'master' });
        }

        if (slideData.themeId) {
            if (
                (type === DataType.SlideMaster || type === DataType.NotesMaster) &&
                this.ctx.idsMap[slideData.themeId]
            ) {
                // master 不可以使用重复的 theme
                const oldTheme = this.ctx.presentation.themeList.find((theme) => theme.id === slideData.themeId) ?? {};
                const newThemeId = this.getId(oldTheme.id, 'theme', true);
                this.ctx.themeList.push(this.migrateTheme({ ...oldTheme, id: newThemeId }));
                newSlide.themeId = this.getId(newThemeId, 'theme');
            } else {
                newSlide.themeId = this.getId(slideData.themeId, 'theme');
            }
        }

        if (slideData.type === 'slide') {
            // undefined or 1 都是 true
            newSlide.show = slideData.show !== 0;
        }

        if (slideData.type === 'layout' || slideData.type === 'slide') {
            // undefined or 1 都是 true
            newSlide.showMasterSp = slideData.showMasterSp !== 0;
        }

        newSlide.cSld = this.migrateCommonSlideData(
            slideData.slideData ?? {
                name: '',
                groupShape: {
                    shapeList: [],
                    type: 'groupShape',
                },
            },
        );

        if (slideData.clrMap) {
            newSlide.clrMap = this.migrateColorMap(slideData.clrMap);
        }

        if (slideData.type !== 'master' && slideData.type !== 'notesMaster') {
            const clrMapOvr: any = {};

            if (slideData?.clrMapOvr?.overrideClrMapping) {
                clrMapOvr.overrideClrMapping = this.migrateColorMap(slideData.clrMapOvr.overrideClrMapping);
            } else {
                clrMapOvr.masterClrMapping = true;
            }

            newSlide.clrMapOvr = clrMapOvr;
        }

        newSlide.timing = this.migrateTiming(slideData.timing, newSlide.id);

        // 需兼容两个 slide 用了同一个 id 的脏数据：只添加一个 note，否则会重复绑定
        if (slideData.noteData && !this.isNoteSlideIdExisted(slideData)) {
            newSlide.noteSlideId = this.getNoteSlideId(slideData);
        }

        if (slideData.headerFooter) {
            newSlide.hf = this.migrateHeaderFooter(slideData.headerFooter);
        }

        const { titleTextStyleList, bodyTextStyleList, otherTextStyleList } = slideData;
        if (titleTextStyleList || bodyTextStyleList || otherTextStyleList) {
            newSlide.txStyles = this.migrateTextStyle(slideData);
        }

        const findGuideListById = (id, guideListData) => {
            return guideListData?.find((guide) => guide.id === id)?.guides ?? [];
        };

        if (type === DataType.SlideLayout) {
            newSlide.slideGuideList = this.migrateGuideList(
                findGuideListById(slideData.id, this.ctx.presentation?.guides?.layoutGuides),
            );
        }

        if (type === DataType.SlideMaster) {
            newSlide.slideGuideList = this.migrateGuideList(
                findGuideListById(slideData.id, this.ctx.presentation?.guides?.masterGuides),
            );
        }

        return newSlide;
    };

    public migrateNoteSlide(slideData) {
        if (slideData.noteData) {
            const id = this.getNoteSlideId(slideData);
            this.ctx.page = { id, type: 'note' };

            return {
                id,
                slideId: this.getPageId(slideData),
                type: DataType.Notes,
                cSld: {
                    name: '',
                    spTree: {
                        id: this.createShapeId('spTree'),
                        type: DataType.GroupShape,
                        nvPr: {},
                        spPr: {
                            xfrm: {
                                off: {
                                    x: 0,
                                    y: 0,
                                },
                                ext: {
                                    cx: 0,
                                    cy: 0,
                                },
                                chOff: {
                                    x: 0,
                                    y: 0,
                                },
                                chExt: {
                                    cx: 0,
                                    cy: 0,
                                },
                            },
                        },
                        shapeList: [
                            merge(this.migrateShape(slideData.noteData.note), {
                                nvPr: { ph: { type: STPlaceholderType.body } },
                            }),
                        ],
                    },
                },
            };
        }
    }

    public migrateTextStyle = (slide) => {
        const newTextStyle: any = {};
        if (!isNil(slide.titleTextStyleList)) {
            newTextStyle.titleStyle = this.migrateTextStyleList(slide.titleTextStyleList);
        }
        if (!isNil(slide.bodyTextStyleList)) {
            newTextStyle.bodyStyle = this.migrateTextStyleList(slide.bodyTextStyleList);
        }
        if (!isNil(slide.otherTextStyleList)) {
            newTextStyle.otherStyle = this.migrateTextStyleList(slide.otherTextStyleList);
        }
        return newTextStyle;
    };

    public migrateHeaderFooter = (headerFooter) => {
        const newHf: any = {};
        if (!isNil(headerFooter.sldNum)) {
            newHf.sldNum = Boolean(headerFooter.sldNum);
        }
        if (!isNil(headerFooter.datetime)) {
            newHf.dt = Boolean(headerFooter.datetime);
        }
        if (!isNil(headerFooter.header)) {
            newHf.hdr = Boolean(headerFooter.header);
        }
        if (!isNil(headerFooter.footer)) {
            newHf.ftr = Boolean(headerFooter.footer);
        }
        return newHf;
    };

    public migrateTimingHierarchy = (v, k, transform) => {
        if (v?.type?.startsWith?.('tn_')) {
            const tnMap = {
                tn_set: () => ({ set: transform(v) }),
                tn_anim: () => ({ anim: transform(v) }),
                tn_animEffect: () => ({ animEffect: transform(v) }),
                tn_animMotion: () => ({ animMotion: transform(v) }),
                tn_animRot: () => ({ animRot: transform(v) }),
                tn_animScale: () => ({ animScale: transform(v) }),
                tn_cmd: () => {
                    const cmd = {
                        cBhvr: v.cBhvr,
                        type: STTLCommandType[v.cmdType],
                        cmd: v.cmdName,
                    };
                    return { cmd: transform(cmd) };
                },
            };
            const { type } = v;
            unset(v, 'type');
            const tn = tnMap[type]?.();
            if (tn) return tn;
        }
        if (!isNil(v?.presetID)) {
            const presetId = v.presetID;
            // eslint-disable-next-line no-param-reassign
            delete v.presetID;
            return { presetId, ...transform(v) };
        }
        if (v?.tavlst) {
            const tavLst = v.tavlst;
            // eslint-disable-next-line no-param-reassign
            delete v.tavlst;
            return { tavLst: { tav: tavLst.filter(Boolean).map((tav) => transform(tav)) }, ...transform(v) };
        }
        // tnBase
        if (v?.cBhvr?.cTn?.by) {
            const { by } = v.cBhvr.cTn;
            // eslint-disable-next-line no-param-reassign
            delete v?.cBhvr?.cTn?.by;
            return { by: String(by), ...transform(v) };
        }
        if (k === 'stCondLst') {
            return { cond: v.map((cond) => transform(cond)).filter(Boolean) };
        }
        if (k === 'childTnLst') {
            return { egTimeNodeChoice: v.map((cond) => transform(cond)).filter(Boolean) };
        }
        if (k === 'attrNameLst') {
            return { attrName: v.map((attr) => transform(attr)) };
        }
        // set
        if (k === 'to' && v.toStrVal) {
            if (v.toStrVal) return { strVal: { val: String(v.toStrVal) } };
            if (v.toFltVal) return { fltVal: { val: v.toFltVal } };
            if (v.toBoolVal) return { fltVal: { val: v.toBoolVal } };
            if (v.toIntVal) return { fltVal: { val: v.toIntVal } };
            if (v.clrVal) return { fltVal: { val: v.clrVal } };
        }
        // anim
        if (v?.strValue) return { strVal: { val: v.strValue.strValue } };
        if (v?.fltValue) return { fltVal: { val: v.fltValue.fltValue } };
        if (v?.toBoolValue) return { boolVal: { val: v.toBoolValue } };
        if (v?.toIntValue) return { intVal: { val: v.toIntValue } };
        if (v?.clrVal) return { clrVal: { val: v.clrVal } };
    };

    public migrateTimingKeySpell = (v, k) => {
        return {
            accumulate: STTLBehaviorAccumulateType[v],
            additive: STTLBehaviorAdditiveType[v],
            calcmode: STTLAnimateBehaviorCalcMode[v],
            clrSpc: STTLAnimateColorSpace[v],
            dir: STTLAnimateColorDirection[v],
            evt: STTLTriggerEvent[v],
            fill: STTLTimeNodeFillType[v],
            fromSeqType: STTLTimeNodeType[v],
            masterRel: STTLTimeNodeMasterRelation[v],
            nodeType: STTLTimeNodeType[v],
            origin: STTLAnimateMotionBehaviorOrigin[v],
            override: STTLBehaviorOverrideType[v],
            pathEditMode: STTLAnimateMotionPathEditMode[v],
            presetClass: STTLTimeNodePresetClassType[v],
            restart: STTLTimeNodeRestartType[v],
            seqType: STTLTimeNodeType[v],
            syncBehavior: STTLTimeNodeSyncType[v],
            transition: STTLAnimateEffectTransition[v],
            val: STTLTriggerRuntimeNode[v],
            valueType: STTLAnimateBehaviorValueType[v],
            xfrmType: STTLBehaviorTransformType[v],
        }[k];
    };

    public transformChildTn = (obj) => {
        return cloneDeepWith(obj, (v, k) => {
            if (isNil(v)) {
                return v;
            }
            if (v.id) {
                unset(v, 'id');
            }
            if (v.spid) {
                // 如果 spid 所指向的 图形不存在，则此动画是冗余动画，不给 spid 分配新 id
                const shapeOriginId = [this.ctx.page?.type, this.ctx.page?.id, v.spid].join(this.idSep);
                if (this.ctx.idsMap[shapeOriginId]) {
                    v.spid = this.getShapeId(v.spid);
                } else {
                    v.spid = '';
                }
            }
            if (k === 'delay' || k === 'dur') {
                return String(v);
            }
            const hierarchyChanged = this.migrateTimingHierarchy(v, k, this.transformChildTn);
            if (hierarchyChanged) {
                return hierarchyChanged;
            }
            const spellChanged = this.migrateTimingKeySpell(v, k);
            if (spellChanged) {
                return spellChanged;
            }
        });
    };

    public migrateTiming = (timingData, pageId: string) => {
        const unitTimeNodeMap = {};
        const mediaReferenceCountMap = {};
        if (timingData?.tmRoot) {
            const getSeqTriggerId = (seq) => {
                const { nodeType } = seq.cTn ?? {};
                if (nodeType === STTLTimeNodeType.mainSeq) {
                    return K_DEFAULT_TRIGGER_ID;
                }
                if (nodeType === STTLTimeNodeType.interactiveSeq) {
                    // 这里不用转换shapeId了，transformChildTn做过了
                    const spid = seq.nextCondLst?.[0].tgtEl?.spTgt?.spid;
                    if (spid) {
                        return spid;
                    }
                    if (seq.nextCondLst) {
                        return K_DEFAULT_TRIGGER_ID;
                    }
                }
            };
            const tnLst = [
                {
                    cTn: {
                        ...timingData.tmRoot,
                        nodeType: STTLTimeNodeType.tmRoot,
                        childTnLst: {
                            egTimeNodeChoice: timingData?.tmRoot?.childTnLst?.filter(Boolean).map((seq) => ({
                                seq: this.transformChildTn(seq),
                            })),
                        },
                    },
                },
            ];
            tnLst[0].cTn?.childTnLst?.egTimeNodeChoice?.forEach(({ seq }) => {
                const triggerId = getSeqTriggerId(seq);
                if (!triggerId) return;

                if (!unitTimeNodeMap[triggerId]) {
                    unitTimeNodeMap[triggerId] = {
                        type: triggerId === K_DEFAULT_TRIGGER_ID ? STTLTimeNodeType.mainSeq : seq.cTn.nodeType,
                        unitTimeNodeList: [],
                    };
                }
                const { unitTimeNodeList } = unitTimeNodeMap[triggerId];
                const clickTimeNodeChoiceGroup = seq.cTn.childTnLst.egTimeNodeChoice;
                for (const clickTimeNodeChoice of clickTimeNodeChoiceGroup) {
                    const startTimeNodeChoiceGroup = clickTimeNodeChoice.cTn.childTnLst.egTimeNodeChoice;
                    for (const startTimeNodeChoice of startTimeNodeChoiceGroup) {
                        const unitTimeNodeChoiceGroup = startTimeNodeChoice.cTn.childTnLst.egTimeNodeChoice;
                        for (const unitTimeNodeChoice of unitTimeNodeChoiceGroup) {
                            const unitTimeNodeData = { par: unitTimeNodeChoice };
                            const shapeId = UnitTimeNode.getShapeId(unitTimeNodeData);
                            if (!shapeId) {
                                continue;
                            }
                            // presetSubtype 必须存在，否则不会产生动画 mutation
                            if (
                                unitTimeNodeData.par?.cTn?.presetId &&
                                unitTimeNodeData.par?.cTn?.presetClass &&
                                !unitTimeNodeData.par?.cTn?.presetSubtype
                            ) {
                                unitTimeNodeData.par.cTn.presetSubtype = 0;
                            }
                            unitTimeNodeList.push(unitTimeNodeData);
                            if (unitTimeNodeData.par?.cTn?.presetClass === STTLTimeNodePresetClassType.mediacall) {
                                const mediaId = `${MEDIA_TRIGGER_ID_PREFIX}${shapeId}`;
                                if (!mediaReferenceCountMap[mediaId]) {
                                    mediaReferenceCountMap[mediaId] = 0;
                                }
                                mediaReferenceCountMap[mediaId] += 1;
                            }
                        }
                    }
                }
            });
        }

        // 迁移音视频的动画信息
        for (const key of Object.keys(this.ctx.mediaIdTimingMap)) {
            unitTimeNodeMap[key] = {
                type: STTLTimeNodeType.interactiveSeq,
                unitTimeNodeList: [{ ...this.ctx.mediaIdTimingMap[key], referenceCount: mediaReferenceCountMap[key] }],
            };
        }
        this.ctx.mediaIdTimingMap = {};

        return { id: `${PATH_TIMING}:${pageId}`, type: DataType.Timing, unitTimeNodeMap };
    };

    public migrateCommonSlideData = (slideData) => {
        const newData = {
            name: slideData.name ?? '',
            spTree: {
                ...this.migrateGroupShape(slideData.groupShape),
                id: this.createShapeId('spTree'),
                nvPr: {},
                spPr: {
                    xfrm: {
                        off: {
                            x: 0,
                            y: 0,
                        },
                        ext: {
                            cx: 0,
                            cy: 0,
                        },
                        chOff: {
                            x: 0,
                            y: 0,
                        },
                        chExt: {
                            cx: 0,
                            cy: 0,
                        },
                    },
                },
            },
            bg: slideData.background && this.migrateSlideBackground(slideData.background),
        };

        return newData;
    };

    public migrateSlideBackground = (slideBackground) => {
        // 3.0 bg 和 bgPr 不可以为 {}, 因此迁移结果为 {} 的话都要转为 undefined
        if (!slideBackground || Object.keys(slideBackground).length <= 0) {
            return undefined;
        }
        const newSlideBackground: any = {};
        const newBgPr = this.migrateBackground(slideBackground);
        if (Object.keys(newBgPr).length <= 0) {
            return undefined;
        }
        newSlideBackground.bgPr = newBgPr;
        return newSlideBackground;
    };

    public migrateGroupShape = (groupShape) => {
        const nvPr: any = this.migrateCommonNonVisualProps({
            props: shallowOmit(groupShape.props, 'placeholder'),
            name: groupShape.name,
            hidden: groupShape.hidden,
            locks: groupShape.locks,
        });
        const spPr: any = this.migrateCommonShapeProperties(groupShape.props);

        return {
            id: this.createShapeId(groupShape.id),
            type: DataType.GroupShape,
            shapeList: (groupShape.shapeList ?? []).map(this.migrateElement).filter(Boolean),
            nvPr,
            spPr,
        };
    };

    public migrateConnector = (connector) => {
        const nvPr: any = this.migrateCommonNonVisualProps({
            props: connector.props,
            name: connector.name,
            hidden: connector.hidden,
            locks: connector.locks,
        });
        // 连接线的 id 在最后会统一处理, 先存一个临时的
        if (!isNil(connector.props?.cxnSpProps)) {
            const { stCxn, endCxn } = connector.props.cxnSpProps;
            if (!isNil(stCxn?.id) || !isNil(stCxn?.index)) {
                nvPr.stCxn = this.migrateConnectorPoint(stCxn);
            }

            if (!isNil(endCxn?.id) || !isNil(endCxn?.index)) {
                nvPr.endCxn = this.migrateConnectorPoint(endCxn);
            }
        }
        const spPr: any = this.migrateCommonShapeProperties(connector.props);

        return {
            id: this.createShapeId(connector.id),
            type: DataType.Connector,
            nvPr,
            spPr,
        };
    };

    public migrateConnectorPoint = (point) => {
        const newPoint: any = {};
        if (!isNil(point.id)) {
            newPoint.id = [this.ctx.page?.type, this.ctx.page?.id, point.id].join(this.idSep);
            this.ctx.connections.push(newPoint);
        }
        if (!isNil(point.index)) {
            newPoint.idx = point.index;
        }
        return newPoint;
    };

    public migratePicture = (picture) => {
        const id = this.createShapeId(picture.id);

        let nvPr: any = this.migrateCommonNonVisualProps({
            props: picture.props,
            name: picture.name,
            hidden: picture.hidden,
            locks: picture.locks,
        });
        if (!isNil(picture.relativeResize)) {
            nvPr.preferRelativeResize = Boolean(picture.relativeResize);
        }

        const spPr: any = this.migrateCommonShapeProperties(picture.props, true);

        let blipFill: any = {};
        if (!isNil(picture.image)) {
            blipFill = this.migratePictureBlipFill(picture.image);
        }

        if (!isNil(picture.audio) || !isNil(picture.video)) {
            nvPr = this.migratePictureMediaNode(picture, nvPr);
        }

        return {
            id,
            type: DataType.Picture,
            nvPr,
            spPr,
            blipFill,
        };
    };

    public migrateLocking = (locks) => {
        const newLock: any = {};
        if (!isNil(locks.noGrp)) {
            newLock.noGrp = Boolean(locks.noGrp);
        }
        if (!isNil(locks.noMove)) {
            newLock.noMove = Boolean(locks.noMove);
        }
        if (!isNil(locks.noChangeAspect)) {
            newLock.noChangeAspect = Boolean(locks.noChangeAspect);
        }
        if (!isNil(locks.noResize)) {
            newLock.noResize = Boolean(locks.noResize);
        }
        if (!isNil(locks.noSelect)) {
            newLock.noSelect = Boolean(locks.noSelect);
        }
        if (!isNil(locks.noAdjustHandles)) {
            newLock.noAdjustHandles = Boolean(locks.noAdjustHandles);
        }
        if (!isNil(locks.noChangeArrowheads)) {
            newLock.noChangeArrowheads = Boolean(locks.noChangeArrowheads);
        }
        if (!isNil(locks.noChangeShapeType)) {
            newLock.noChangeShapeType = Boolean(locks.noChangeShapeType);
        }
        if (!isNil(locks.noCrop)) {
            newLock.noCrop = Boolean(locks.noCrop);
        }
        if (!isNil(locks.noEditPoints)) {
            newLock.noEditPoints = Boolean(locks.noEditPoints);
        }
        if (!isNil(locks.noRot)) {
            newLock.noRot = Boolean(locks.noRot);
        }
        if (!isNil(locks.noUngrp)) {
            newLock.noUngrp = Boolean(locks.noUngrp);
        }
        if (!isNil(locks.noTextEdit)) {
            newLock.noTextEdit = Boolean(locks.noTextEdit);
        }
        return newLock;
    };

    public migratePictureBlipFill = (image) => {
        const newBlipFill: any = {};
        if (!isNil(image.src)) {
            const newSrcId = this.addImageResource(image.src);
            if (newSrcId) {
                image.src = newSrcId;
            }
        }
        if (!isNil(image.blip)) {
            newBlipFill.blip = this.migratePictureBlip(image.blip, image.src);
        }
        if (isNil(image.blip) && !isNil(image.src)) {
            newBlipFill.blip = { embed: image.src };
        }
        if (!isNil(image.srcRect)) {
            newBlipFill.srcRect = this.migratePictureRect(image.srcRect);
        }
        if (!isNil(image.stretch) || !isNil(image.tile)) {
            newBlipFill.egFillModeProperties = this.migratePictureMode(image);
        } else {
            // egFillModeProperties 必填，否则导出会报修复
            newBlipFill.egFillModeProperties = this.migratePictureMode({ stretch: { fillRect: {} } });
        }
        if (!isNil(image.rotWithShape)) {
            newBlipFill.rotWithShape = Boolean(image.rotWithShape);
        }
        return newBlipFill;
    };

    // 音视频迁移脚本
    public migratePictureMediaNode = (picture, nvPr) => {
        if (!picture.audio && !picture.video) {
            return nvPr;
        }

        const { presentation } = this.ctx;
        const oldMedia = picture.audio || picture.video;
        const mediaType = picture.audio ? 'audioResource' : 'videoResource';

        // 1. 迁移 mediaInfo 信息，这部分 XSD 中没有的
        // 2. link 为空，不添加至 resourceMap
        if (presentation?.resource?.[mediaType]?.resourceMap && oldMedia.link) {
            presentation.resource[mediaType].resourceMap[oldMedia.link] = {
                src: oldMedia.link,
                format: oldMedia.format,
                size: oldMedia.size,
                isExternal: Boolean(oldMedia.isExternal),
            };
        }

        // 迁移杂糅在这里面的动画相关信息，这部分数据需要放到动画中
        const commonMediaNode = {
            cTn: oldMedia.tmNode,
            vol: oldMedia.volume,
            mute: Boolean(oldMedia.mute),
            numSld: oldMedia.slideCount,
            showWhenStopped: Boolean(oldMedia.showWhenStopped),
        };

        if (!isNil(picture.video)) {
            const timeNodeChoice = {
                video: {
                    cMediaNode: commonMediaNode,
                    fullScrn: Boolean(picture.video.fullScrn),
                },
            };
            this.ctx.mediaIdTimingMap[`${MEDIA_TRIGGER_ID_PREFIX}${this.getShapeId(picture.id)}`] = timeNodeChoice;
        }

        if (!isNil(picture.audio)) {
            const timeNodeChoice = {
                audio: {
                    cMediaNode: commonMediaNode,
                },
            };
            this.ctx.mediaIdTimingMap[`${MEDIA_TRIGGER_ID_PREFIX}${this.getShapeId(picture.id)}`] = timeNodeChoice;
        }

        return {
            ...nvPr,
            egMedia: {
                videoFile: picture.video && {
                    // 若 videoFile 存在，则 link 必须存在
                    link: oldMedia.link ?? '',
                },
                audioFile: picture.audio && {
                    link: oldMedia.link ?? '',
                },
            },
        };
    };

    public migratePictureRect = (srcRect) => {
        const newRect: any = {};
        if (!isNil(srcRect.b)) {
            newRect.b = this.getNumberWithMinPrecision(srcRect.b);
        }
        if (!isNil(srcRect.l)) {
            newRect.l = this.getNumberWithMinPrecision(srcRect.l);
        }
        if (!isNil(srcRect.r)) {
            newRect.r = this.getNumberWithMinPrecision(srcRect.r);
        }
        if (!isNil(srcRect.t)) {
            newRect.t = this.getNumberWithMinPrecision(srcRect.t);
        }
        return newRect;
    };

    public migratePictureMode = (image) => {
        const newMode: any = {};
        if (!isNil(image.tile)) {
            newMode.tile = this.migrateTile(image.tile);
        }
        if (!isNil(image.stretch)) {
            newMode.stretch = this.migrateStretch(image.stretch);
        }
        return newMode;
    };

    public migrateTile = (tile) => {
        const newTile: any = {};
        if (!isNil(tile.tx)) {
            newTile.tx = tile.tx;
        }
        if (!isNil(tile.ty)) {
            newTile.ty = tile.ty;
        }
        if (!isNil(tile.sx)) {
            newTile.sx = tile.sx;
        }
        if (!isNil(tile.sy)) {
            newTile.sy = tile.sy;
        }
        if (!isNil(tile.flip)) {
            newTile.flip = STTileFlipMode[tile.flip];
        }
        if (!isNil(tile.algn)) {
            newTile.algn = STRectAlignment[tile.algn];
        }
        return newTile;
    };

    public migrateStretch = (stretch) => {
        const newfillRect: any = {};
        if (!isNil(stretch.fillRect)) {
            newfillRect.fillRect = this.migratePictureRect(stretch.fillRect);
        }
        return newfillRect;
    };

    public migratePictureBlip = (blip, defaultSrc) => {
        const newBlip: any = {};
        if (!isNil(blip.src) || !isNil(defaultSrc)) {
            const newSrcId = this.addImageResource(blip.src, blip.imageType);
            if (newSrcId) {
                blip.src = newSrcId;
            }
            newBlip.embed = newSrcId ?? defaultSrc;
        }
        if (!isNil(blip.backupSrc)) {
            const newSrcId = this.addImageResource(blip.backupSrc);
            if (newSrcId) {
                blip.backupSrc = newSrcId;
            }
        }
        if (!isNil(blip.alphaModFix)) {
            newBlip.alphaModFix = [{ amt: blip.alphaModFix }];
        }
        if (!isNil(blip.contrast) || !isNil(blip.brightness)) {
            newBlip.lum = [{ contrast: blip.contrast, bright: blip.brightness }];
        }
        // 重新着色
        if (!isNil(blip.grayscl) && blip.grayscl !== false) {
            newBlip.grayscl = [blip.grayscl];
        }
        // blip.biLevel = 0 是 2.0 的错误数据，直接过滤
        if (!isNil(blip.biLevel) && blip.biLevel !== 0) {
            newBlip.biLevel = [{ thresh: blip.biLevel }];
        }
        if (!isNil(blip.luminance)) {
            newBlip.lum = [this.migrateLuminance(blip.luminance)];
        }
        if (!isNil(blip.duotone)) {
            newBlip.duotone = [this.migrateDuotone(blip.duotone)];
        }

        if (
            Object.keys(blip).some((i) =>
                ['backupSrc', 'colorTemperature', 'saturate', 'sharpenSoften', 'contrast', 'brightness'].includes(i),
            )
        ) {
            newBlip.extLst = {};
            newBlip.extLst.ext = [{ imgProps: [{ imgLayer: this.migrateImgLayer(blip) }] }];
        }
        return newBlip;
    };

    public migrateLuminance = (luminance) => {
        const newLuminance: any = {};
        if (!isNil(luminance.brightness)) {
            newLuminance.bright = luminance.brightness;
        }
        if (!isNil(luminance.contrast)) {
            newLuminance.contrast = luminance.contrast;
        }
        return newLuminance;
    };

    public migrateDuotone = (duotone) => {
        const newDuotone: any = {};
        if (!isNil(duotone.clr1) || !isNil(duotone.clr2)) {
            newDuotone.egColorChoice = [];
        }
        if (!isNil(duotone.clr1)) {
            newDuotone.egColorChoice.push(this.migrateColor(duotone.clr1));
        }
        if (!isNil(duotone.clr2)) {
            newDuotone.egColorChoice.push(this.migrateColor(duotone.clr2));
        }
        return newDuotone;
    };

    public migrateImgLayer = (blip) => {
        const newImgLayer: any = {};
        if (!isNil(blip.backupSrc)) {
            newImgLayer.embed = blip.backupSrc;
        }
        const { colorTemperature, saturate, sharpenSoften, contrast, brightness } = blip;
        if (colorTemperature || saturate || sharpenSoften || contrast || brightness) {
            newImgLayer.imgEffect = [this.migrateImgEffect(blip)];
        }
        return newImgLayer;
    };

    public migrateImgEffect = (blip) => {
        const newImgEffect: any = {};
        if (!isNil(blip.colorTemperature)) {
            newImgEffect.colorTemperature = { colorTemp: blip.colorTemperature };
        }
        if (!isNil(blip.saturate)) {
            newImgEffect.saturation = { sat: String(blip.saturate) };
        }
        if (!isNil(blip.sharpenSoften)) {
            newImgEffect.sharpenSoften = { amount: blip.sharpenSoften };
        }
        if (!isNil(blip.contrast) || !isNil(blip.brightness)) {
            newImgEffect.brightnessContrast = { contrast: blip.contrast, bright: blip.brightness };
        }
        return newImgEffect;
    };

    public migrateTable = (table) => {
        const nvPr: any = this.migrateCommonNonVisualProps({
            props: table.props,
            name: table.name,
            hidden: table.hidden,
            locks: table.locks,
        });

        const spPr: any = this.migrateCommonShapeProperties(table.props);

        const graphic: any = {};
        if (table.graphicData) {
            graphic.graphicData = this.migrateGraphicTableData(table.graphicData);
        }

        return {
            id: this.createShapeId(table.id),
            type: DataType.GraphicalObjectFrameTable,
            nvPr,
            spPr,
            graphic,
        };
    };

    public migrateGraphicTableData = (graphicData) => {
        return { tbl: graphicData.tbl && this.migrateTableData(graphicData.tbl) };
    };

    public migrateTableData = (tbl) => {
        const newTbl: any = {};
        // tableStyleId 在迁移的时候必定存在，因此 tblPr 也必定存在
        newTbl.tblPr = this.migrateTablePr(tbl.tblPr ?? {});
        if (!isNil(tbl.tblColList)) {
            newTbl.tblGrid = { gridCol: tbl.tblColList.map((i) => ({ w: i?.w ?? 0 })) };
        }
        if (!isNil(tbl.tblRowList)) {
            newTbl.tr = tbl.tblRowList.map((i) => this.migrateTableRow(i));
        }
        return newTbl;
    };

    public migrateTableRow = (tr) => {
        const newTr: any = {};
        if (isNil(tr)) {
            return newTr;
        }
        // TODO: 2.0 数据 id 未转换
        if (!isNil(tr.h)) {
            newTr.h = tr.h;
        }
        if (!isNil(tr.tcList)) {
            newTr.tc = tr.tcList.map((i) => this.migrateTableCell(i));
        }
        return newTr;
    };

    public migrateTableCell = (tc) => {
        const newTc: any = {};
        if (isNil(tc)) {
            return newTc;
        }
        if (!isNil(tc.gridSpan)) {
            newTc.gridSpan = tc.gridSpan;
        }
        if (!isNil(tc.rowSpan)) {
            newTc.rowSpan = tc.rowSpan;
        }
        if (!isNil(tc.hMerge)) {
            newTc.hMerge = Boolean(tc.hMerge);
        }
        if (!isNil(tc.vMerge)) {
            newTc.vMerge = Boolean(tc.vMerge);
        }
        if (!isNil(tc.textBody)) {
            newTc.txBody = this.migrateTextBody(tc.textBody);
        }
        if (!isNil(tc.tcPr)) {
            newTc.tcPr = this.migrateTableCellPr(tc.tcPr);
        }
        return newTc;
    };

    public migrateTableCellPr = (tcPr) => {
        const newTcPr: any = {};
        Object.assign(newTcPr, this.migrateBackground(tcPr));
        if (!isNil(tcPr.lnT)) {
            newTcPr.lnT = this.migrateOutline(tcPr.lnT)?.ln;
        }
        if (!isNil(tcPr.lnB)) {
            newTcPr.lnB = this.migrateOutline(tcPr.lnB)?.ln;
        }
        if (!isNil(tcPr.lnR)) {
            newTcPr.lnR = this.migrateOutline(tcPr.lnR)?.ln;
        }
        if (!isNil(tcPr.lnL)) {
            newTcPr.lnL = this.migrateOutline(tcPr.lnL)?.ln;
        }
        if (!isNil(tcPr.lnTlToBr)) {
            newTcPr.lnTlToBr = this.migrateOutline(tcPr.lnTlToBr)?.ln;
        }
        if (!isNil(tcPr.lnBlToTr)) {
            newTcPr.lnBlToTr = this.migrateOutline(tcPr.lnBlToTr)?.ln;
        }
        if (!isNil(tcPr.vert)) {
            newTcPr.vert = STTextVerticalType[tcPr.vert];
        }
        if (!isNil(tcPr.anchor)) {
            newTcPr.anchor = STTextAnchoringType[tcPr.anchor];
        }
        if (!isNil(tcPr.anchorCtr)) {
            newTcPr.anchorCtr = Boolean(tcPr.anchorCtr);
        }
        if (!isNil(tcPr.marB)) {
            newTcPr.marB = tcPr.marB;
        }
        if (!isNil(tcPr.marL)) {
            newTcPr.marL = tcPr.marL;
        }
        if (!isNil(tcPr.marR)) {
            newTcPr.marR = tcPr.marR;
        }
        if (!isNil(tcPr.marT)) {
            newTcPr.marT = tcPr.marT;
        }
        return newTcPr;
    };

    public migrateTablePr = (tblPr) => {
        const newTblPr: any = {};
        if (!isNil(tblPr.effectList)) {
            newTblPr.effectLst = this.migrateEffectLst(tblPr.effectList);
        }
        if (!isNil(tblPr.firstRow)) {
            newTblPr.firstRow = Boolean(tblPr.firstRow);
        }
        if (!isNil(tblPr.firstCol)) {
            newTblPr.firstCol = Boolean(tblPr.firstCol);
        }
        if (!isNil(tblPr.bandRow)) {
            newTblPr.bandRow = Boolean(tblPr.bandRow);
        }
        if (!isNil(tblPr.bandCol)) {
            newTblPr.bandCol = Boolean(tblPr.bandCol);
        }
        if (!isNil(tblPr.lastRow)) {
            newTblPr.lastRow = Boolean(tblPr.lastRow);
        }
        if (!isNil(tblPr.lastCol)) {
            newTblPr.lastCol = Boolean(tblPr.lastCol);
        }
        if (!isNil(tblPr.rtl)) {
            newTblPr.rtl = Boolean(tblPr.rtl);
        }
        newTblPr.tableStyleId = tblPr.styleId ?? '{58542034-FE4F-4ADA-92B8-4CA66D0F0DF3}';
        return newTblPr;
    };

    public migrateGraphicalFrameChart = (chart) => {
        const nvPr: any = this.migrateCommonNonVisualProps({
            props: chart.props,
            name: chart.name,
            hidden: chart.hidden,
            locks: chart.locks,
        });

        const spPr: any = this.migrateCommonShapeProperties(chart.props);

        return {
            id: this.getShapeId(chart.id),
            type: DataType.GraphicalObjectFrameChart,
            nvPr,
            spPr,
            chartSpace: chart.graphicData?.chart && this.migrateChartSpace(chart.graphicData.chart),
        };
    };

    public migrateChartSpace = (chartData) => {
        const chartSpace: any = {};

        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            props: ['spPr', this.migrateCommonShapeProperties],
            textBody: ['txPr', this.migrateTextBody],
            style: ['style'],
            round: ['roundedCorners', this.migrateNumberToCTBoolean],
            date1904: ['date1904', this.migrateNumberToCTBoolean],
            lang: ['lang'],
        };
        this.migrateFromMap(chartData, chartSpace, migrateMap);
        chartSpace.chart = this.migrateChart(chartData);

        return chartSpace;
    };

    // chartSpace.chart的数据
    public migrateChart = (chartData) => {
        const chart: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            title: ['title', this.migrateChartTitle],
            plotArea: ['plotArea', this.migrateChartPlotArea],
            legend: ['legend', this.migrateChartLegend],
            view3D: ['view_3d', this.migrateView3D],
            backWall: ['backWall', this.migrateWall],
            sideWall: ['sideWall', this.migrateWall],
            floor: ['floor', this.migrateWall],
            dispBlanksAs: ['dispBlanksAs', this.migrateEnumStringToEnum(STDispBlanksAs, STDispBlanksAs.PBDEFAULT)],
            plotVisOnly: ['plotVisOnly', this.migrateNumberToCTBoolean],
        };
        this.migrateFromMap(chartData, chart, migrateMap);

        return chart;
    };

    public migrateChartTitle = (title) => {
        const newTitle: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            overlay: ['overlay', this.migrateNumberToCTBoolean],
            props: ['spPr', this.migrateCommonShapeProperties],
            textBody: ['txPr', this.migrateTextBody],
            layout: ['layout', this.migrateChartLayout],
            chartText: ['tx', this.migrateChartTitleText],
        };
        this.migrateFromMap(title, newTitle, migrateMap);
        return newTitle;
    };

    public migrateChartTitleText = (text) => {
        const newText: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            textBody: ['rich', this.migrateTextBody],
        };
        this.migrateFromMap(text, newText, migrateMap);
        return newText;
    };

    public migrateChartLayout = (layout) => {
        const newLayout: any = {};
        const manualLayout: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            leftMode: ['xMode', this.migrateEnumStringToEnum(STLayoutMode, STLayoutMode.PBDEFAULT)],
            left: ['x'],
            topMode: ['yMode', this.migrateEnumStringToEnum(STLayoutMode, STLayoutMode.PBDEFAULT)],
            top: ['y'],
            widthMode: ['wMode', this.migrateEnumStringToEnum(STLayoutMode, STLayoutMode.PBDEFAULT)],
            width: ['w'],
            heightMode: ['hMode', this.migrateEnumStringToEnum(STLayoutMode, STLayoutMode.PBDEFAULT)],
            height: ['h'],
            layoutTarget: ['layoutTarget', this.migrateEnumStringToEnum(STLayoutTarget, STLayoutTarget.PBDEFAULT)],
        };

        this.migrateFromMap(layout, manualLayout, migrateMap);
        newLayout.manualLayout = manualLayout;

        return newLayout;
    };

    public migrateChartPlotArea = (plotArea) => {
        const newPlotArea: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            layout: ['layout', this.migrateChartLayout],
            props: ['spPr', this.migrateCommonShapeProperties],
            charts: [
                'charts',
                (v) =>
                    this.migratePlotCharts(v, {
                        categoryAxes: plotArea.categoryAxes,
                        dateAxes: plotArea.dateAxes,
                        valueAxes: plotArea.valueAxes,
                    }),
            ],
        };

        this.migrateFromMap(plotArea, newPlotArea, migrateMap);
        return newPlotArea;
    };

    public migrateChartType = (type) => {
        // SubChartType 引了是undefined，先不花时间分析，改成数值
        switch (type) {
            case 'bar':
                return SubChartType.BarChart;
            case 'line':
                return SubChartType.LineChart;
            case 'area':
                return SubChartType.AreaChart;
            case 'pie':
                return SubChartType.PieChart;
            case 'doughnut':
                return SubChartType.DoughnutChart;
            case 'radar':
                return SubChartType.RadarChart;
            case 'scatter':
                return SubChartType.ScatterChart;
            case 'stock':
                return SubChartType.StockChart;
            case 'bar3d':
                return SubChartType.Bar3DChart;
            case 'pie3d':
                return SubChartType.Pie3DChart;
            case 'line3d':
                return SubChartType.Line3DChart;
            case 'ofpie':
                return SubChartType.OfPieChart;
            case 'area3d':
                return SubChartType.Area3DChart;
            case 'surface':
                return SubChartType.SurfaceChart;
            case 'surface3d':
                return SubChartType.Surface3DChart;
            case 'bubble':
                return SubChartType.BubbleChart;
        }
    };

    public migratePlotCharts = (plotCharts, axesMap) => {
        return plotCharts?.map((subChart) => {
            const newSubChart: any = {};
            this.migrateSubChartSharedProps(subChart, newSubChart, axesMap);

            const { type } = subChart;
            switch (type) {
                case 'bar':
                case 'bar3d':
                    return this.migrateBarChart(subChart, newSubChart);
                case 'line':
                case 'line3d':
                    return this.migrateLineChart(subChart, newSubChart);
                case 'area':
                case 'area3d':
                    return this.migrateAreaChart(subChart, newSubChart);
                case 'pie':
                case 'pie3d':
                    return this.migratePieChart(subChart, newSubChart);
                case 'doughnut':
                    return this.migrateDoughnutChart(subChart, newSubChart);
                case 'radar':
                    return this.migrateRadarChart(subChart, newSubChart);
                case 'scatter':
                    return this.migrateSatterChart(subChart, newSubChart);
                case 'stock':
                    return this.migrateStockChart(subChart, newSubChart);
                case 'ofpie':
                    return this.migrateOfpieChart(subChart, newSubChart);
                case 'bubble':
                    return this.migrateBubbleChart(subChart, newSubChart);
                default:
                    return this.migrateBarChart(subChart, newSubChart);
            }
        });
    };

    public migrateSer = (ser, subChart) => {
        return ser.map((serData) => {
            const { type } = subChart;
            switch (type) {
                case 'bar':
                case 'bar3d':
                    return this.migrateBarSer(serData);
                case 'line':
                case 'line3d':
                case 'stock':
                    return this.migrateLineSer(serData);
                case 'area':
                case 'area3d':
                    return this.migrateAreaSer(serData);
                case 'pie':
                case 'pie3d':
                case 'doughnut':
                case 'ofpie':
                    return this.migratePieSer(serData);
                case 'radar':
                    return this.migrateRadarSer(serData);
                case 'scatter':
                    return this.migrateScatterSer(serData);
                case 'bubble':
                    return this.migrateBubbleSer(serData);
                default:
                    return this.migrateBarSer(serData);
            }
        });
    };

    public migrateSubChartSharedProps = (subChart, newSubChart, axesMap) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            type: ['type', this.migrateChartType],
            // axisId不转，因为后面直接把axes挂到了subChart上
            dataLabels: ['dLbls', this.migrateDLbls],
            varyColors: ['varyColors', this.migrateNumberToCTBoolean],
            series: ['ser', (v) => this.migrateSer(v, subChart)],
        };
        this.migrateFromMap(subChart, newSubChart, migrateMap);
        // 将2.0的PlotArea的axes放到subChart中
        const { categoryAxes, dateAxes, valueAxes } = axesMap;
        [categoryAxes, dateAxes, valueAxes].forEach((axes, index) => {
            const isCatAx = index === 0;
            const isDateAx = index === 1;
            const isValAx = index === 2;

            if (axes) {
                this.migrateAxes(axes)?.forEach((axis) => {
                    if (axis && subChart.axisIds?.find((id) => id === axis.axId?.val)) {
                        newSubChart.axes = newSubChart.axes || [];
                        if (isCatAx) {
                            newSubChart.axes.push({
                                catAx: axis,
                            });
                        } else if (isDateAx) {
                            newSubChart.axes.push({
                                dateAx: axis,
                            });
                        } else if (isValAx) {
                            newSubChart.axes.push({
                                valAx: axis,
                            });
                        }
                    }
                });
            }

            return newSubChart;
        });
    };

    public migrateBarChart = (barChart, newBarChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            overlap: ['overlap'],
            gapWidth: ['gapWidth'],
            direction: ['barDir', this.migrateEnumStringToEnum(STBarDir, STBarDir.PBDEFAULT)],
            grouping: ['grouping', this.migrateEnumStringToEnum(STBarGrouping, STBarGrouping.PBDEFAULT)],
        };

        // 按原来的数据结构，在subChart中多了axesAddIndex, axes
        this.migrateFromMap(barChart, newBarChart, migrateMap);
        return newBarChart;
    };

    public migrateLineChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            marker: ['marker', this.migrateNumberToCTBoolean],
            smooth: ['smooth', this.migrateNumberToCTBoolean],
            grouping: ['grouping', this.migrateEnumStringToEnum(STGrouping, STGrouping.PBDEFAULT)],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migrateAreaChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            grouping: ['grouping', this.migrateEnumStringToEnum(STGrouping, STGrouping.PBDEFAULT)],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migratePieChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            grouping: ['grouping', this.migrateEnumStringToEnum(STGrouping, STGrouping.PBDEFAULT)],
            firstSliceAngle: ['firstSliceAng'],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migrateDoughnutChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            grouping: ['grouping', this.migrateEnumStringToEnum(STGrouping, STGrouping.PBDEFAULT)],
            firstSliceAngle: ['firstSliceAng'],
            holeSize: ['holeSize'],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migrateRadarChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            radarStyle: ['radarStyle', this.migrateEnumStringToEnum(STRadarStyle, STRadarStyle.PBDEFAULT)],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migrateSatterChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            scatterStyle: ['scatterStyle', this.migrateEnumStringToEnum(STScatterStyle, STScatterStyle.PBDEFAULT)],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migrateStockChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            highLowLines: ['hiLowLines', this.migrateChartLines],
            upDownBars: ['upDownBars', this.migrateUpDownBars],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migrateUpDownBars = (upDownBars) => {
        const newUpDownBars: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            gapWidth: ['gapWidth'],
            upBars: ['upBars', (v) => ({ spPr: this.migrateCommonShapeProperties(v) })],
            downBars: ['downBars', (v) => ({ spPr: this.migrateCommonShapeProperties(v) })],
        };
        this.migrateFromMap(upDownBars, newUpDownBars, migrateMap);
        return newUpDownBars;
    };
    public migrateOfpieChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            ofPieType: ['ofPieType', this.migrateEnumStringToEnum(STOfPieType, STOfPieType.PBDEFAULT)],
            gapWidth: ['gapWidth'],
            splitType: ['splitType', this.migrateEnumStringToEnum(STSplitType, STSplitType.PBDEFAULT)],
            secondPieSize: ['secondPieSize'],
            splitPos: ['splitPos'],
            secondPiePts: ['custSplit', this.migrateCustSplit],
            serLines: ['serLines', this.migrateSerLines],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migrateBubbleChart = (subChart, newSubChart) => {
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            bubble3D: ['bubble3D', this.migrateNumberToCTBoolean],
            bubbleScale: ['bubbleScale'],
            showNegBubbles: ['showNegBubbles', this.migrateNumberToCTBoolean],
            sizeRepresents: [
                'sizeRepresents',
                this.migrateEnumStringToEnum(STSizeRepresents, STSizeRepresents.PBDEFAULT),
            ],
        };

        this.migrateFromMap(subChart, newSubChart, migrateMap);
        return newSubChart;
    };

    public migrateSerSharedProps = (serShared) => {
        const newSerShared: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            idx: ['idx'],
            order: ['order'],
            seriesText: ['tx', this.migrateSeriesText],
            props: ['spPr', this.migrateCommonShapeProperties],
            dataLabels: ['dLbls', this.migrateDLbls],
            trendline: ['trendline', this.migrateTrendlines],
            errorBars: ['errBars', this.migrateErrBars],
            categories: ['categories', this.migrateCategories],
            valueCount: ['valueCount', this.migrateWithOriginValue],
            categoryAxisData: ['categoryAxisData', this.migrateWithOriginValue],
            values: ['values', this.migrateWithOriginValue],
            otherDPt: ['otherDPt', this.migrateDPt],
            otherDLbl: ['otherDLbl', this.migrateDLbl],
            marker: ['marker', this.migrateMarker],
        };
        this.migrateFromMap(serShared, newSerShared, migrateMap);
        // disable这个值一定有，不管原来2.0有没有
        newSerShared.disable = !!serShared.disable;
        // categories 一定有，默认值 []
        if (isNil(newSerShared.categories)) {
            newSerShared.categories = [];
        }
        return newSerShared;
    };

    public migrateBarSer = (ser) => {
        const newSer: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            invertIfNegative: ['invertIfNegative', this.migrateNumberToCTBoolean],
            shape: ['shape', this.migrateEnumStringToEnum(STShape, STShape.PBDEFAULT)],
        };
        this.migrateFromMap(ser, newSer, migrateMap);
        return {
            ...this.migrateSerSharedProps(ser),
            ...newSer,
        };
    };

    public migrateLineSer = (ser) => {
        const newSer: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            marker: ['marker', this.migrateMarker],
            smooth: ['smooth', this.migrateNumberToCTBoolean],
        };
        this.migrateFromMap(ser, newSer, migrateMap);
        return {
            ...this.migrateSerSharedProps(ser),
            ...newSer,
        };
    };

    public migrateAreaSer = (ser) => {
        const newSer: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {};
        this.migrateFromMap(ser, newSer, migrateMap);
        return {
            ...this.migrateSerSharedProps(ser),
            ...newSer,
        };
    };

    public migratePieSer = (ser) => {
        const newSer: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {};
        this.migrateFromMap(ser, newSer, migrateMap);
        return {
            ...this.migrateSerSharedProps(ser),
            ...newSer,
        };
    };

    public migrateRadarSer = (ser) => {
        const newSer: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            marker: ['marker', this.migrateMarker],
        };
        this.migrateFromMap(ser, newSer, migrateMap);
        return {
            ...this.migrateSerSharedProps(ser),
            ...newSer,
        };
    };

    public migrateScatterSer = (ser) => {
        const newSer: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            marker: ['marker', this.migrateMarker],
            smooth: ['smooth', this.migrateNumberToCTBoolean],
        };
        this.migrateFromMap(ser, newSer, migrateMap);
        return {
            ...this.migrateSerSharedProps(ser),
            ...newSer,
        };
    };

    public migrateBubbleSer = (ser) => {
        const newSer: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            invertIfNegative: ['invertIfNegative', this.migrateNumberToCTBoolean],
            bubble3D: ['bubble3D', this.migrateNumberToCTBoolean],
            bubbleSize: ['bubbleSize', this.migrateNumDataSource],
        };
        this.migrateFromMap(ser, newSer, migrateMap);
        return {
            ...this.migrateSerSharedProps(ser),
            ...newSer,
        };
    };

    public migrateCategories = (categories) => {
        return categories.map((category, index) => {
            const newCategory: any = {};
            const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
                categoryPoint: ['categoryPoint', this.migrateValuePoint],
                valuePoint: ['valuePoint', this.migrateValuePoint],
                dataLabel: ['dataLabel', this.migrateDLbl],
                dataPoint: ['dataPoint', this.migrateDPt],
            };
            this.migrateFromMap(category, newCategory, migrateMap);
            // sdk 3.0新增的idx属性，用于props自身判定索引
            newCategory.idx = index;
            return newCategory;
        });
    };

    public migrateValuePoint = (point) => {
        const newPoint: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            value: ['v', this.migrateWithOriginValue],
            formatCode: ['formatCode', this.migrateWithOriginValue],
        };
        this.migrateFromMap(point, newPoint, migrateMap);
        return newPoint;
    };

    public migrateDPt = (dPt) => {
        const newDPt: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            idx: ['idx'],
            props: ['spPr', this.migrateCommonShapeProperties],
            explosion: ['explosion'],
            bubble3D: ['bubble3D', this.migrateNumberToCTBoolean],
            invertIfNegative: ['invertIfNegative', this.migrateNumberToCTBoolean],
            marker: ['marker', this.migrateMarker],
        };
        this.migrateFromMap(dPt, newDPt, migrateMap);
        return newDPt;
    };

    public migrateMarker = (marker) => {
        const newMarker: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            symbol: ['symbol', this.migrateEnumStringToEnum(STMarkerStyleDmlC, STMarkerStyleDmlC.PBDEFAULT)],
            size: ['size'],
            props: ['spPr', this.migrateCommonShapeProperties],
        };
        this.migrateFromMap(marker, newMarker, migrateMap);
        return newMarker;
    };

    public migrateSeriesText = (seriesText) => {
        const newSeriesText: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            value: ['v', this.migrateWithOriginValue],
            strRef: ['strRef', this.migrateStrRef], // todo 等category完了补下这里
        };
        this.migrateFromMap(seriesText, newSeriesText, migrateMap);
        return newSeriesText;
    };

    public migrateStrRef = (strRef) => {
        const newStrRef: any = {};
        newStrRef.strCache = {
            ptCount: {
                val: strRef.count,
            },
            pt:
                strRef.points?.map((p) => ({
                    v: p.value,
                    idx: p.idx,
                })) ?? [],
        };
        return newStrRef;
    };

    public migrateWithOriginValue = (v) => {
        return v;
    };

    public migrateNumDataSource = (numDataSource) => {
        const newNumDataSource: any = {};
        newNumDataSource.numLit = {
            formatCode: numDataSource.formatCode,
            ptCount: {
                val: numDataSource.count,
            },
            pt: numDataSource.points?.map((p) => ({
                v: p.value,
                idx: p.idx,
                formatCode: p.formatCode,
            })),
        };
        newNumDataSource.numRef = {
            numCache: newNumDataSource.numLit,
        };
        return newNumDataSource;
    };

    public migrateSerLines = (serLines) => {
        return [this.migrateChartLines({ props: serLines })];
    };

    public migrateErrBars = (errBars) => {
        return errBars.map((errBar) => {
            const newErrBar: any = {};
            const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
                errDir: ['errDir', this.migrateEnumStringToEnum(STErrDir, STErrDir.PBDEFAULT)],
                errBarType: ['errBarType', this.migrateEnumStringToEnum(STErrBarType, STErrBarType.PBDEFAULT)],
                errValType: ['errValType', this.migrateEnumStringToEnum(STErrValType, STErrValType.PBDEFAULT)],
                noEndCap: ['noEndCap', this.migrateNumberToCTBoolean],
                props: ['spPr', this.migrateCommonShapeProperties],
                plus: ['plus'], // todo 这里的类型是ICTNumDataSource，等ser数据写完补一下
                minus: ['minus'], // todo 这里的类型是ICTNumDataSource，等ser数据写完补一下
            };
            this.migrateFromMap(errBar, newErrBar, migrateMap);
            return newErrBar;
        });
    };

    public migrateTrendlines = (trendlineList) => {
        return (Array.isArray(trendlineList) ? trendlineList : [trendlineList]).map(this.migrateTrendline);
    };

    public migrateTrendline = (trendline) => {
        const newTrendline: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            name: ['name', this.migrateWithOriginValue],
            type: ['trendlineType', this.migrateEnumStringToEnum(STTrendlineType, STTrendlineType.PBDEFAULT)],
            props: ['spPr', this.migrateCommonShapeProperties],
            order: ['order'],
            period: ['period'],
            forward: ['forward'],
            backward: ['backward'],
            intercept: ['intercept'],
            dispRSqr: ['dispRSqr', this.migrateNumberToCTBoolean],
            dispEq: ['dispEq', this.migrateNumberToCTBoolean],
            label: ['trendlineLbl', this.migrateTrendlineLbl],
        };
        this.migrateFromMap(trendline, newTrendline, migrateMap);
        return newTrendline;
    };

    public migrateTrendlineLbl = (trendlineLbl) => {
        const newTrendlineLbl: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            numFmt: ['numFmt', this.migrateNumFmt],
        };
        this.migrateFromMap(trendlineLbl, newTrendlineLbl, migrateMap);
        return {
            ...newTrendlineLbl,
            ...this.migrateChartTitle(trendlineLbl),
        };
    };

    public migrateNumFmt = (numFmt) => {
        const newNumFmt: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            formatCode: ['formatCode', this.migrateWithOriginValue],
            sourceLinked: ['sourceLinked', (v) => !!v],
        };
        this.migrateFromMap(numFmt, newNumFmt, migrateMap);
        return newNumFmt;
    };

    public migrateDLbls = (dLbls) => {
        const newDLbls: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            dataLabel: ['dLbl', this.migrateDLbl],
        };
        this.migrateFromMap(dLbls, newDLbls, migrateMap);
        return {
            ...this.migrateDLblSharedProps(dLbls),
            ...newDLbls,
        };
    };

    public migrateDLbl = (dLbl) => {
        const newDLbl: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            idx: ['idx'],
            layout: ['layout', this.migrateChartLayout],
            text: ['tx', this.migrateChartTitleText],
        };
        this.migrateFromMap(dLbl, newDLbl, migrateMap);
        return {
            ...this.migrateDLblSharedProps(dLbl),
            ...newDLbl,
        };
    };

    public migrateDLblSharedProps = (dLblProps) => {
        const newDLblProps: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            delete: ['deleteKeyword', this.migrateNumberToCTBoolean],
            numFmt: ['numFmt', this.migrateNumFmt],
            props: ['spPr', this.migrateCommonShapeProperties],
            textBody: ['txPr', this.migrateTextBody],
            labelPosition: ['dLblPos', this.migrateEnumStringToEnum(STDLblPos, STDLblPos.PBDEFAULT)],
            showLegendKey: ['showLegendKey', this.migrateNumberToCTBoolean],
            showVal: ['showVal', this.migrateNumberToCTBoolean],
            showCategoryName: ['showCatName', this.migrateNumberToCTBoolean],
            showSeriesName: ['showSerName', this.migrateNumberToCTBoolean],
            showPercent: ['showPercent', this.migrateNumberToCTBoolean],
            showBubbleSize: ['showBubbleSize', this.migrateNumberToCTBoolean],
            separator: ['separator', this.migrateWithOriginValue],
            showLeaderLines: ['showLeaderLines', this.migrateNumberToCTBoolean],
            leaderLines: ['leaderLines', (v) => ({ spPr: this.migrateCommonShapeProperties(v) })],
        };
        this.migrateFromMap(
            {
                ...dLblProps,
                // 之前2.0的DLbl的show相关的字段，为了减少冗余字段，在导入为1的字段，导出时把所有的缺省的字段都用0补齐
                // 而3.0的导出不会补齐，这里需要按导出逻辑，在migrate的时候补齐所有缺省的字段
                showLegendKey: dLblProps.showLegendKey ?? 0,
                showVal: dLblProps.showVal ?? 0,
                showCategoryName: dLblProps.showCategoryName ?? 0,
                showSeriesName: dLblProps.showSeriesName ?? 0,
                showPercent: dLblProps.showPercent ?? 0,
                showBubbleSize: dLblProps.showBubbleSize ?? 0,
            },
            newDLblProps,
            migrateMap,
        );
        return newDLblProps;
    };

    public migrateAxId = (axisIds) => {
        return axisIds.map((axisId) => {
            return this.migrateSimpleTypePropsToComplexType(axisId);
        });
    };

    public migrateAxes = (axes) => {
        return axes.filter(Boolean).map((axis) => {
            const newAxis: any = {};
            const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
                axId: ['axId'],
                autoLabeled: ['autoKeyword', this.migrateNumberToCTBoolean],
                scaling: ['scaling', this.migrateScaling],
                delete: ['deleteKeyword', this.migrateNumberToCTBoolean],
                position: ['axPos', this.migrateEnumStringToEnum(STAxPos, STAxPos.PBDEFAULT)],
                majorGridlines: ['majorGridlines', this.migrateChartLines],
                minorGridlines: ['minorGridlines', this.migrateChartLines],
                title: ['title', this.migrateChartTitle],
                numFmt: ['numFmt', this.migrateNumFmt],
                majorTickMark: ['majorTickMark', this.migrateEnumStringToEnum(STTickMark, STTickMark.PBDEFAULT)],
                minorTickMark: ['minorTickMark', this.migrateEnumStringToEnum(STTickMark, STTickMark.PBDEFAULT)],
                tickLabelPosition: ['tickLblPos', this.migrateEnumStringToEnum(STTickLblPos, STTickLblPos.PBDEFAULT)],
                props: ['spPr', this.migrateCommonShapeProperties],
                textBody: ['txPr', this.migrateTextBody],
                crossAx: ['crossAx'],
                crosses: ['crosses', this.migrateEnumStringToEnum(STCrosses, STCrosses.PBDEFAULT)],
                crossesAt: ['crossesAt'],
                crossBetween: ['crossBetween', this.migrateEnumStringToEnum(STCrossBetween, STCrossBetween.PBDEFAULT)],
                lblAlgn: ['lblAlgn', this.migrateEnumStringToEnum(STLblAlgn, STLblAlgn.PBDEFAULT)],
                lblOffset: ['lblOffset'],
                tickLblSkip: ['tickLblSkip'],
                tickMarkSkip: ['tickMarkSkip'],
                majorUnit: ['majorUnit'],
                minorUnit: ['minorUnit'],
                dispUnits: ['dispUnits', this.migrateDispUnits],
                // 日期坐标轴独有
                baseTimeUnit: ['baseTimeUnit', this.migrateEnumStringToEnum(STTimeUnit, STTimeUnit.PBDEFAULT)],
                majorTimeUnit: ['majorTimeUnit', this.migrateEnumStringToEnum(STTimeUnit, STTimeUnit.PBDEFAULT)],
                minorTimeUnit: ['minorTimeUnit', this.migrateEnumStringToEnum(STTimeUnit, STTimeUnit.PBDEFAULT)],
            };

            this.migrateFromMap(axis, newAxis, migrateMap);
            return newAxis;
        });
    };

    public migrateScaling = (scaling) => {
        const newScaling: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            orientation: ['orientation', this.migrateEnumStringToEnum(STOrientationDmlC, STOrientationDmlC.PBDEFAULT)],
            max: ['max'],
            min: ['min'],
            logBase: ['logBase'],
        };
        this.migrateFromMap(scaling, newScaling, migrateMap);
        return newScaling;
    };

    public migrateDispUnits = (dispUnits) => {
        const newDispUnits: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            dispUnitsLbl: ['dispUnitsLbl', this.migrateChartTitle],
            unit: ['builtInUnit', this.migrateEnumStringToEnum(STBuiltInUnit, STBuiltInUnit.PBDEFAULT)],
        };
        this.migrateFromMap(dispUnits, newDispUnits, migrateMap);
        return newDispUnits;
    };

    public migrateChartLines = (line) => {
        const newLine: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            props: ['spPr', this.migrateCommonShapeProperties],
        };
        this.migrateFromMap(line, newLine, migrateMap);
        return newLine;
    };

    public migrateChartLegend = (legend) => {
        const newLegend: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            legendPosition: ['legendPos', this.migrateEnumStringToEnum(STLegendPos, STLegendPos.PBDEFAULT)],
            overlay: ['overlay', this.migrateNumberToCTBoolean],
            props: ['spPr', this.migrateCommonShapeProperties],
            textBody: ['txPr', this.migrateTextBody],
            layout: ['layout', this.migrateChartLayout],
            legendEntry: ['legendEntry', this.migrateLegendEntry],
        };

        this.migrateFromMap(legend, newLegend, migrateMap);
        return newLegend;
    };

    public migrateLegendEntry = (legendEntry: any[]) => {
        return legendEntry.map((entry) => {
            const newEntry: any = {};
            const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
                idx: ['idx'],
                delete: ['deleteKeyword', this.migrateNumberToCTBoolean],
                textBody: ['txPr', this.migrateTextBody],
            };
            this.migrateFromMap(entry, newEntry, migrateMap);
            return newEntry;
        });
    };

    public migrateCustSplit = (secondPiePts) => {
        const custSplit: any = {};
        custSplit.secondPiePt = secondPiePts.map((secondPiePt) => ({ val: secondPiePt }));
        return custSplit;
    };

    public migrateEnumStringToEnum = <EnumType>(
        enumObject: EnumType,
        defaultVal: any,
    ): ((val: any) => { val: EnumType }) => {
        return (enumKey: any) => {
            return { val: enumObject[enumKey] || defaultVal };
        };
    };

    public migrateView3D = (view3D) => {
        const newView3D: any = {};
        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            rotX: ['rotX'],
            rotY: ['rotY'],
            depthPercent: ['depthPercent'],
            hPercent: ['hPercent'],
            rAngAx: ['rAngAx', this.migrateNumberToCTBoolean],
            perspective: ['perspective'],
        };
        this.migrateFromMap(view3D, newView3D, migrateMap);

        return newView3D;
    };

    public migrateWall = (wall) => {
        const newWall: any = {};

        const migrateMap: Record<string, [string, ((val: any) => any)?]> = {
            props: ['spPr', this.migrateCommonShapeProperties],
            thickness: ['thickness'],
        };
        this.migrateFromMap(wall, newWall, migrateMap);

        return newWall;
    };

    public migrateSimpleTypePropsToComplexType = (val: any) => {
        return {
            val,
        };
    };

    public migrateAllPropsToComplexType = (val: any) => {
        return Object.keys(val).reduce((result, curKey) => {
            return {
                ...result,
                [curKey]: {
                    val: val[curKey],
                },
            };
        }, {});
    };

    public migrateNumberToCTBoolean = (number: number | null): ICTBooleanDmlC => {
        return {
            val: !!number,
        };
    };

    public migrateFromMap = (
        oldData: any,
        newData: any,
        migrateMap: Record<string, [string, ((val: any) => any)?]>,
        defaultMigrateFunction: (val: any) => any = this.migrateSimpleTypePropsToComplexType,
    ) => {
        Object.keys(migrateMap).forEach((oldKey) => {
            const oldValue = oldData[oldKey];
            if (!isNil(oldValue)) {
                const [newKey, migrate] = migrateMap[oldKey];
                newData[newKey] = migrate ? migrate(oldValue) : defaultMigrateFunction(oldValue);
            }
        });
    };

    public migrateElement = (element) => {
        if (isNil(element)) {
            return;
        }
        if (element.type === 'shape') {
            // 删除 notesMaster 里 placeholder.type 不存在的图形
            if (
                this.ctx.page?.type === 'notesMaster' &&
                element.props?.placeholder &&
                isNil(element.props.placeholder.type)
            ) {
                return undefined;
            }
            return this.migrateShape(element);
        }

        if (element.type === 'groupShape') {
            return this.migrateGroupShape(element);
        }

        if (element.type === 'cxnSp') {
            return this.migrateConnector(element);
        }

        if (element.type === 'pic') {
            return this.migratePicture(element);
        }

        if (element.type === 'graphFrame_Tbl') {
            return this.migrateTable(element);
        }

        if (element.type === 'chart') {
            return this.migrateGraphicalFrameChart(element);
        }

        return;
    };

    public migrateShape = (shape) => {
        const nvPr = this.migrateCommonNonVisualProps({
            props: shape.props,
            name: shape.name,
            hidden: shape.hidden,
            locks: shape.locks,
        });
        if (shape.props?.txBox) {
            nvPr.txBox = Boolean(shape.props.txBox);
        }

        const spPr: any = this.migrateCommonShapeProperties(shape.props, !shape.props?.placeholder);

        return {
            id: this.createShapeId(shape.id),
            type: DataType.Shape,
            nvPr,
            spPr,
            txBody: shape.textBody && this.migrateTextBody(shape.textBody),
            useBgFill: shape.useBgFill && Boolean(shape.useBgFill),
        };
    };

    public migrateCommonNonVisualProps = (options) => {
        const { props, name, hidden, locks } = options;
        const newNvPr: any = {};
        if (props?.placeholder) {
            newNvPr.ph = {
                idx: props.placeholder?.index,
                type: STPlaceholderType[props?.placeholder?.type],
                hasCustomPrompt: props?.placeholder?.hasCustomPrompt && Boolean(props.placeholder.hasCustomPrompt),
            };
            // master 里不能有 ctrTitle 的占位符，转换为 title
            if (this.ctx.page?.type === 'master' && newNvPr.ph.type === STPlaceholderType.ctrTitle) {
                newNvPr.ph.type = STPlaceholderType.title;
            }
        }
        if (props?.hlink) {
            newNvPr.hlinkClick = this.migrateHLink(props.hlink);
        }
        if (props?.txBox) {
            newNvPr.txBox = Boolean(props.txBox);
        }
        if (props?.attachment) {
            newNvPr.attachment = this.migrateAttachment(props.attachment);
        }
        if (props?.addon) {
            newNvPr.addon = this.migrateAddon(props.addon);
        }
        if (props?.textMath) {
            newNvPr.isEquationShape = Boolean(props.textMath);
        }
        if (name) {
            newNvPr.name = name;
        }
        if (hidden) {
            newNvPr.hidden = Boolean(hidden);
        }
        if (locks) {
            newNvPr.locking = this.migrateLocking(locks);
        }
        return newNvPr;
    };

    public migrateAddon = (addon) => {
        const newAddon: any = {};
        if (!isNil(addon.displayMode)) {
            newAddon.displayMode = STAddonMode[addon.displayMode];
        }
        if (!isNil(addon.embedUrl)) {
            newAddon.embedUrl = addon.embedUrl;
        }
        if (!isNil(addon.extraData)) {
            if (typeof addon.extraData === 'string') {
                newAddon.extraData = addon.extraData;
            } else {
                newAddon.extraData = JSON.stringify(addon.extraData);
            }
        }
        if (!isNil(addon.iframeId)) {
            newAddon.iframeId = addon.iframeId;
        }
        if (!isNil(addon.originUrl)) {
            newAddon.originUrl = addon.originUrl;
        }
        if (!isNil(addon.title)) {
            newAddon.title = addon.title;
        }
        if (!isNil(addon.aid)) {
            newAddon.aid = addon.aid;
        }
        if (!isNil(addon.type)) {
            newAddon.type = String(addon.type);
        }
        if (!isNil(addon.docElement)) {
            newAddon.docElement = this.migrateAddonDocElement(addon.docElement);
        }
        return newAddon;
    };

    public migrateAddonDocElement = (docElement) => {
        const newDocElement: any = {};
        if (!isNil(docElement.type)) {
            newDocElement.type = docElement.type;
        }
        if (!isNil(docElement.addonId)) {
            newDocElement.addonId = docElement.addonId;
        }
        if (!isNil(docElement.anchorId)) {
            newDocElement.anchorId = docElement.anchorId;
        }
        if (!isNil(docElement.embedUrl)) {
            newDocElement.embedUrl = docElement.embedUrl;
        }
        if (!isNil(docElement.extraData)) {
            newDocElement.extraData = docElement.extraData;
        }
        if (!isNil(docElement.height)) {
            newDocElement.height = docElement.height;
        }
        if (!isNil(docElement.width)) {
            newDocElement.width = docElement.width;
        }
        if (!isNil(docElement.lockAspectRatio)) {
            newDocElement.lockAspectRatio = docElement.lockAspectRatio;
        }
        if (!isNil(docElement.originUrl)) {
            newDocElement.originUrl = docElement.originUrl;
        }
        if (!isNil(docElement.packageId)) {
            newDocElement.packageId = docElement.packageId;
        }
        if (!isNil(docElement.src)) {
            newDocElement.src = docElement.src;
        }
        if (!isNil(docElement.sourceType)) {
            newDocElement.sourceType = docElement.sourceType;
        }
        return newDocElement;
    };

    public migrateAttachment = (attachment) => {
        const newAttachment: any = {};
        if (!isNil(attachment.id)) {
            newAttachment.id = attachment.id;
        }
        if (!isNil(attachment.name)) {
            newAttachment.name = attachment.name;
        }
        if (!isNil(attachment.type)) {
            newAttachment.type = attachment.type;
        }
        if (!isNil(attachment.classify)) {
            newAttachment.classify = STAttachmentClassify[attachment.classify];
        }
        if (!isNil(attachment.showType)) {
            newAttachment.showType = STAttachmentShowType[attachment.showType];
        }
        if (!isNil(attachment.size)) {
            newAttachment.size = attachment.size;
        }
        if (!isNil(attachment.objKey)) {
            newAttachment.objKey = attachment.objKey;
        }
        return newAttachment;
    };

    public migrateCommonShapeProperties = (shapeProps, needPrst = false) => {
        const spPr: any = {};
        if (!isNil(shapeProps?.frame)) {
            spPr.xfrm = this.migrateFrame(shapeProps.frame);
        }

        Object.assign(spPr, this.migrateBackground(shapeProps?.background));
        Object.assign(spPr, this.migrateOutline(shapeProps?.outline));

        if (!isNil(shapeProps?.prstGeom)) {
            spPr.prstGeom = this.migratePrstGeom(shapeProps.prstGeom);
        }
        if (!isNil(shapeProps?.customGeom)) {
            spPr.custGeom = this.migrateCustomGeom(shapeProps.customGeom);
        }
        // custGeom 和 prstGeom 有必须存在的情况，补充默认值
        if (needPrst && isNil(shapeProps?.prstGeom) && isNil(shapeProps?.customGeom)) {
            spPr.prstGeom = { prst: STShapeType.rect };
        }
        if (!isNil(shapeProps?.effectList)) {
            spPr.effectLst = this.migrateEffectLst(shapeProps.effectList);
        }
        if (!isNil(shapeProps?.scene3d)) {
            spPr.scene3d = this.migrateScene3d(shapeProps.scene3d);
        }
        if (!isNil(shapeProps?.shape3d)) {
            spPr.sp3d = this.migrateShape3d(shapeProps.shape3d);
        }
        return spPr;
    };

    public migrateShape3d = (shape3d) => {
        const newShape3d: any = {};
        if (!isNil(shape3d.bevelTop)) {
            newShape3d.bevelT = this.migrateBevel3d(shape3d.bevelTop);
        }
        if (!isNil(shape3d.bevelBottom)) {
            newShape3d.bevelB = this.migrateBevel3d(shape3d.bevelBottom);
        }
        if (!isNil(shape3d.extrusionColor)) {
            newShape3d.extrusionClr = this.migrateColor(shape3d.extrusionColor);
        }
        if (!isNil(shape3d.materialPreset)) {
            newShape3d.prstMaterial = STPresetMaterialType[shape3d.materialPreset];
        }
        if (!isNil(shape3d.extrusionHeight)) {
            newShape3d.extrusionH = shape3d.extrusionHeight;
        }
        return newShape3d;
    };

    public migrateBevel3d = (bevel3d) => {
        const newBevel3d: any = {};
        if (!isNil(bevel3d.width)) {
            newBevel3d.w = bevel3d.width;
        }
        if (!isNil(bevel3d.height)) {
            newBevel3d.h = bevel3d.height;
        }
        if (!isNil(bevel3d.preset)) {
            newBevel3d.prst = STBevelPresetType[bevel3d.preset];
        }
        return newBevel3d;
    };

    public migrateScene3d = (scene3d) => {
        const newScene3d: any = {};
        // scene3d 存在，则 camera 和 lightRig 必须存在
        newScene3d.camera = this.migrateCamera(scene3d.camera ?? {});
        newScene3d.lightRig = this.migrateLightRig(scene3d.lightRig ?? {}, scene3d.direction);
        return newScene3d;
    };

    public migrateLightRig = (lightRig, lightDirection) => {
        const newLightRig: any = {};
        if (!isNil(lightRig.rotation)) {
            newLightRig.rot = this.migrateRotation3D(lightRig.rotation);
        }
        // LightRig 存在时，rig 和 dir 必须存在
        newLightRig.rig = STLightRigType[lightRig.rigPreset] ?? STLightRigType.threePt;
        newLightRig.dir = STLightRigDirection[lightDirection] ?? STLightRigDirection.t;
        return newLightRig;
    };

    public migrateCamera = (camera) => {
        const newCamera: any = {};
        if (!isNil(camera.rotation)) {
            newCamera.rot = this.migrateRotation3D(camera.rotation);
        }
        // camera 存在，则 prst 必须存在
        newCamera.prst = STPresetCameraType[camera.preset] ?? STPresetCameraType.orthographicFront;
        return newCamera;
    };

    public migrateRotation3D = (rotation) => {
        const newRotation: any = {};
        if (!isNil(rotation.x)) {
            // 旋转角度最大为 360 * 60000
            newRotation.lat = rotation.x % 21600000; // 纬度
        }
        if (!isNil(rotation.y)) {
            newRotation.lon = rotation.y % 21600000; // 经度
        }
        if (!isNil(rotation.z)) {
            newRotation.rev = rotation.z % 21600000; // 旋转
        }
        return newRotation;
    };

    public migratePrstGeom = (prstGeom) => {
        const newPrstGeom: any = {};
        // 若 prstGeom 存在，则 prst 必定存在
        newPrstGeom.prst = STShapeType[prstGeom.prstGeomType] ?? STShapeType.rect;
        if (!isNil(prstGeom.adjList)) {
            const gdList = prstGeom.adjList.map((adjOriginVal, index) => {
                const adjVal = this.getPrstGeomAdjVal(adjOriginVal);
                const adjName = this.getPrstGeomAdjName(
                    prstGeom.adjList?.length,
                    index,
                    prstGeom.prstGeomType ?? 'rect',
                    prstGeom.adjNameList,
                );
                return {
                    name: adjName,
                    fmla: adjVal,
                };
            });
            newPrstGeom.avLst = { gd: gdList };
        }
        return newPrstGeom;
    };

    public migrateHLink = (hLink) => {
        const newHLink: any = {};
        // 内部超链接
        if (!isNil(hLink.slideJump)) {
            newHLink.id = hLink.slideJump.target;
            newHLink.action = hLink.slideJump.action;
        }
        // 外部超链接
        if (!isNil(hLink.url)) {
            newHLink.id = hLink.url;
        }
        if (!isNil(hLink.mimeType)) {
            newHLink.mimeType = hLink.mimeType;
        }
        if (!isNil(hLink.iconMode)) {
            newHLink.iconMode = Boolean(hLink.iconMode);
        }
        if (!isNil(hLink.tooltip)) {
            newHLink.tooltip = hLink.tooltip;
        }
        if (!isNil(hLink.colorType)) {
            newHLink.extLst = { ext: [{ hlinkClr: [{ val: STHyperlinkColor[hLink.colorType] }] }] };
        }
        return newHLink;
    };

    public migrateCustomGeom = (customGeom) => {
        // customGeom 不可以为 {}
        if (Object.keys(customGeom).length <= 0) {
            return;
        }
        const newCustomGeom: any = {};
        if (!isNil(customGeom.pathList)) {
            newCustomGeom.pathLst = {
                path: customGeom.pathList.filter(Boolean).map((path) => this.migratePath(path)),
            };
        }
        if (!isNil(customGeom.rect)) {
            newCustomGeom.rect = this.migrateRect(customGeom.rect);
        }
        return newCustomGeom;
    };

    public migratePath = (path) => {
        if (isNil(path)) {
            return;
        }
        const newPath: any = {};
        if (!isNil(path.width)) {
            newPath.w = path.width;
        }
        if (!isNil(path.height)) {
            newPath.h = path.height;
        }
        if (!isNil(path.fill)) {
            newPath.fill = STPathFillMode[path.fill];
        }
        if (!isNil(path.stroke)) {
            newPath.stroke = Boolean(path.stroke);
        }
        if (!isNil(path.pathString)) {
            newPath.pathString = path.pathString;
        }
        return newPath;
    };

    public migrateRect = (rect) => {
        const newRect: any = {};
        if (!isNil(rect.l)) {
            newRect.l = { geomGuideName: rect.l };
        }
        if (!isNil(rect.r)) {
            newRect.r = { geomGuideName: rect.r };
        }
        if (!isNil(rect.b)) {
            newRect.b = { geomGuideName: rect.b };
        }
        if (!isNil(rect.t)) {
            newRect.t = { geomGuideName: rect.t };
        }
        return newRect;
    };

    public migrateEffectLst = (effectList) => {
        const newEffectList: any = {};
        if (!isNil(effectList.glow)) {
            newEffectList.glow = this.migrateEffectGlow(effectList.glow);
        }
        if (!isNil(effectList.outerShadow)) {
            newEffectList.outerShdw = this.migrateEffectOuterShadow(effectList.outerShadow);
        }
        if (!isNil(effectList.innerShadow)) {
            newEffectList.innerShdw = this.migrateEffectInnerShadow(effectList.innerShadow);
        }
        if (!isNil(effectList.reflection)) {
            newEffectList.reflection = this.migrateEffectReflection(effectList.reflection);
        }
        if (!isNil(effectList.softEdge)) {
            newEffectList.softEdge = this.migrateEffectSoftEdge(effectList.softEdge);
        }
        return newEffectList;
    };

    public migrateEffectGlow = (glow) => {
        const newGlow: any = {};
        if (!isNil(glow.rad)) {
            newGlow.rad = glow.rad;
        }
        if (!isNil(glow.glowColor?.color)) {
            newGlow.egColorChoice = this.migrateColor(glow.glowColor.color);
        }
        return newGlow;
    };

    public migrateEffectOuterShadow = (outerShadow) => {
        const newOuterShadow: any = {};
        if (!isNil(outerShadow.algn)) {
            newOuterShadow.algn = STRectAlignment[outerShadow.algn];
        }
        if (!isNil(outerShadow.blurRad)) {
            newOuterShadow.blurRad = outerShadow.blurRad;
        }
        if (!isNil(outerShadow.dir)) {
            newOuterShadow.dir = outerShadow.dir;
        }
        if (!isNil(outerShadow.dist)) {
            newOuterShadow.dist = outerShadow.dist;
        }
        if (!isNil(outerShadow.kx)) {
            newOuterShadow.kx = outerShadow.kx;
        }
        if (!isNil(outerShadow.ky)) {
            newOuterShadow.ky = outerShadow.ky;
        }
        if (!isNil(outerShadow.sx)) {
            newOuterShadow.sx = outerShadow.sx;
        }
        if (!isNil(outerShadow.sy)) {
            newOuterShadow.sy = outerShadow.sy;
        }
        if (!isNil(outerShadow.shadowColor?.color)) {
            newOuterShadow.egColorChoice = this.migrateColor(outerShadow.shadowColor.color);
        }
        return newOuterShadow;
    };

    public migrateEffectInnerShadow = (InnerShadow) => {
        if (Object.keys(InnerShadow).length <= 0) {
            return;
        }
        const newInnerShadow: any = {};
        if (!isNil(InnerShadow.blurRad)) {
            newInnerShadow.blurRad = InnerShadow.blurRad;
        }
        if (!isNil(InnerShadow.dir)) {
            newInnerShadow.dir = InnerShadow.dir;
        }
        if (!isNil(InnerShadow.dist)) {
            newInnerShadow.dist = InnerShadow.dist;
        }
        if (!isNil(InnerShadow.shadowColor?.color)) {
            newInnerShadow.egColorChoice = this.migrateColor(InnerShadow.shadowColor.color);
        }
        return newInnerShadow;
    };

    public migrateEffectReflection = (reflection) => {
        const newOuterShadow: any = {};
        if (!isNil(reflection.algn)) {
            newOuterShadow.algn = STRectAlignment[reflection.algn];
        }
        if (!isNil(reflection.blurRad)) {
            newOuterShadow.blurRad = reflection.blurRad;
        }
        if (!isNil(reflection.dir)) {
            newOuterShadow.dir = reflection.dir;
        }
        if (!isNil(reflection.dist)) {
            newOuterShadow.dist = reflection.dist;
        }
        if (!isNil(reflection.kx)) {
            newOuterShadow.kx = reflection.kx;
        }
        if (!isNil(reflection.ky)) {
            newOuterShadow.ky = reflection.ky;
        }
        if (!isNil(reflection.sx)) {
            newOuterShadow.sx = reflection.sx;
        }
        if (!isNil(reflection.sy)) {
            newOuterShadow.sy = reflection.sy;
        }

        if (!isNil(reflection.endA)) {
            newOuterShadow.endA = reflection.endA;
        }
        if (!isNil(reflection.endPos)) {
            newOuterShadow.endPos = reflection.endPos;
        }
        if (!isNil(reflection.stA)) {
            newOuterShadow.stA = reflection.stA;
        }
        if (!isNil(reflection.rotWithShape)) {
            newOuterShadow.rotWithShape = Boolean(reflection.rotWithShape);
        }
        return newOuterShadow;
    };

    public migrateEffectSoftEdge = (softEdge) => {
        const newSoftEdge: any = {};
        if (!isNil(softEdge.rad)) {
            newSoftEdge.rad = softEdge.rad;
        }
        return newSoftEdge;
    };

    public migrateFrame = (frame) => {
        // 兼容 2.0 数据: {} 认为是 undefined
        if (Object.keys(frame).length === 0) {
            return;
        }
        const xfrm: any = {};

        if (!isNil(frame.left) || !isNil(frame.right)) {
            xfrm.off = {
                x: Math.floor(frame.left) || 0,
                y: Math.floor(frame.top) || 0,
            };
        }

        if (!isNil(frame.width) || !isNil(frame.height)) {
            xfrm.ext = {
                cx: Math.floor(frame.width) || 0,
                cy: Math.floor(frame.height) || 0,
            };
        }

        if (!isNil(frame.childOffsetLeft) || !isNil(frame.childOffsetTop)) {
            xfrm.chOff = {
                x: Math.floor(frame.childOffsetLeft) || 0,
                y: Math.floor(frame.childOffsetTop) || 0,
            };
        }

        if (!isNil(frame.childWidth) || !isNil(frame.childHeight)) {
            xfrm.chExt = {
                cx: Math.floor(frame.childWidth) || 0,
                cy: Math.floor(frame.childHeight) || 0,
            };
        }

        if (!isNil(frame.rotate)) {
            xfrm.rot = frame.rotate;
        }

        if (!isNil(frame.flipH)) {
            xfrm.flipH = !!frame.flipH;
        }

        if (!isNil(frame.flipV)) {
            xfrm.flipV = !!frame.flipV;
        }

        return xfrm;
    };

    public migrateBackground = (background) => {
        if (!background) {
            return;
        }

        const fillProps: any = {};

        // 3.0 无法实现背景部分继承，shadeToTitle = 0 必须完全去掉
        if (!isNil(background.shadeToTitle) && !!background.shadeToTitle) {
            fillProps.shadeToTitle = Boolean(background.shadeToTitle);
        }

        if (background.solidFill?.color) {
            fillProps.solidFill = {
                egColorChoice: this.migrateColor(background.solidFill.color),
            };
        }

        if (background.noFill !== undefined) {
            fillProps.noFill = !!background.noFill;
        }

        if (background.grpFill !== undefined) {
            fillProps.grpFill = !!background.grpFill;
        }

        if (!isNil(background.image)) {
            fillProps.blipFill = this.migratePictureBlipFill(background.image);
        }

        if (background.gradFill) {
            const { gsList, flip, rotateWithShape, tileRect, pathGradient, lin } = background.gradFill;
            const gradFill: any = {};

            if (tileRect) {
                gradFill.tileRect = { ...tileRect };
            }

            if (flip) {
                gradFill.flip = STTileFlipMode[flip];
            }

            if (rotateWithShape !== undefined) {
                gradFill.rotWithShape = !!rotateWithShape;
            }

            if (pathGradient || lin) {
                gradFill.egShadeProperties = {
                    path: pathGradient && {
                        fillToRect: { ...pathGradient.fillToRect },
                        path: STPathShadeType[pathGradient.path],
                    },
                    lin: lin && {
                        ang: lin.ang,
                        scaled: Boolean(lin.scaled),
                    },
                };
            }

            if (gsList) {
                gradFill.gsLst = {
                    gs: gsList.filter(Boolean).map((gs) => ({
                        egColorChoice: this.migrateColor(gs.color),
                        pos: gs.pos,
                    })),
                };
            }

            fillProps.gradFill = gradFill;
        }

        if (background.pattFill) {
            const pattFill: any = {};
            const { fgColor, bgColor, preset } = background.pattFill;
            if (fgColor) {
                pattFill.fgClr = this.migrateColor(fgColor.color);
            }

            if (bgColor) {
                pattFill.bgClr = this.migrateColor(bgColor.color);
            }

            if (preset) {
                pattFill.prst = STPresetPatternVal[preset];
            }

            fillProps.pattFill = pattFill;
        }

        return fillProps;
    };

    public migrateOutline = (outline) => {
        if (!outline) {
            return;
        }

        const lineProps: any = { ln: {} };

        if (outline.noFill === 1 || outline.solidFill || outline.gradFill) {
            lineProps.ln = { egLineFillProperties: this.migrateBackground(outline) };
        }

        if (!isNil(outline.width)) {
            lineProps.ln.w = outline.width;
        }

        if (!isNil(outline.cmpd)) {
            lineProps.ln.cmpd = STCompoundLine[outline.cmpd];
        }

        if (!isNil(outline.style)) {
            lineProps.ln.egLineDashProperties = {
                prstDash: {
                    val: STPresetLineDashVal[outline.style],
                },
            };
        }

        if (!isNil(outline.cap)) {
            lineProps.ln.cap = STLineCap[outline.cap];
        }

        if (!isNil(outline.lineJoin)) {
            if (outline.lineJoin === 'miter') {
                lineProps.ln.egLineJoinProperties = {
                    miter: { lim: 800000 },
                };
            } else {
                lineProps.ln.egLineJoinProperties = {
                    [outline.lineJoin]: true,
                };
            }
        }

        if (!isNil(outline.headEnd)) {
            lineProps.ln.headEnd = this.migrateLineEnd(outline.headEnd);
        }

        if (!isNil(outline.tailEnd)) {
            lineProps.ln.tailEnd = this.migrateLineEnd(outline.tailEnd);
        }

        if (!isNil(outline.sketchStyle)) {
            lineProps.ln.extLst = { ext: [{ lineSketchStyleProps: [this.migrateSketchStyle(outline.sketchStyle)] }] };
        }

        return lineProps;
    };

    public migrateSketchStyle = (sketchStyle) => {
        const newSketchStyle: any = {};
        if (!isNil(sketchStyle.type)) {
            newSketchStyle.type = { egLineSketchType: { [sketchStyle.type]: {} } };
        }
        if (!isNil(sketchStyle.prstGeom)) {
            newSketchStyle.egGeometry = { prstGeom: this.migratePrstGeom(sketchStyle.prstGeom) };
        }
        if (!isNil(sketchStyle.customGeom)) {
            newSketchStyle.egGeometry = { custGeom: this.migrateCustomGeom(sketchStyle.customGeom) };
        }
        return newSketchStyle;
    };

    public migrateLineEnd = (lineEnd) => {
        return {
            type: STLineEndType[lineEnd.lineEndType],
            w: STLineEndWidth[lineEnd.widthType],
            len: STLineEndLength[lineEnd.lengthType],
        };
    };

    public migrateColorMap = (clrMap) => {
        if (!clrMap) {
            return;
        }

        const newColorMap = clrMap;
        for (const key of Object.keys(clrMap)) {
            newColorMap[key] = STColorSchemeIndex[clrMap[key]];
        }

        return newColorMap;
    };

    public migrateColor = (color) => {
        const egColorChoice: any = {};
        if (isNil(color)) {
            return egColorChoice;
        }
        const { colorType, colorValue, srgb_color: srgbColor } = color;

        let clr: any = null;

        if (isNil(colorType) && srgbColor) {
            clr = egColorChoice.srgbClr = {
                // 使用 fromRgbHexString 防止不合法字符出现，比如 color = 'undefined'
                val: this.fromRgbHexString(srgbColor ?? '000000'),
            };
        }

        if (colorType === 'rgb') {
            clr = egColorChoice.srgbClr = {
                // 使用 fromRgbHexString 防止不合法字符出现，比如 color = 'undefined'
                val: this.fromRgbHexString(colorValue ?? '000000'),
            };
        }

        if (colorType === 'scheme') {
            clr = egColorChoice.schemeClr = {
                val: STSchemeColorVal[colorValue],
            };
        }

        const egColorTransform: any = [];
        const colorTransformTypes = [
            'lumMod',
            'lumOff',
            'tint',
            'shade',
            'comp',
            'inv',
            'gray',
            'alpha',
            'alphaOff',
            'alphaMod',
            'hue',
            'hueOff',
            'hueMod',
            'sat',
            'satMod',
            'satOff',
            'lum',
            'red',
            'redOff',
            'redMod',
            'green',
            'greenOff',
            'greenMod',
            'blue',
            'blueOff',
            'blueMod',
            'gamma',
            'invGamma',
        ];

        colorTransformTypes.forEach((transform) => {
            if (!isNil(color[transform])) {
                const val = transform === 'lumOff' ? Math.max(0, Number(color[transform])) : color[transform];
                egColorTransform.push({
                    [transform]: typeof val === 'number' ? { val } : val,
                });
            }
        });

        if (clr && egColorTransform.length) {
            clr.egColorTransform = egColorTransform;
        }

        return egColorChoice;
    };

    public migrateTheme = (theme) => {
        const newTheme: any = { id: this.getId(theme.id, 'theme'), type: DataType.Theme };
        newTheme.name =
            this.ctx.presentation.masterList.filter(Boolean).find((i) => i.themeId === theme.id)?.themeName ??
            '自定义主题';
        if (isNil(theme)) {
            return newTheme;
        }
        newTheme.themeElements = {
            ...ThemeData.themeElements,
        };
        if (!isNil(theme.fontScheme)) {
            newTheme.themeElements.fontScheme = this.migrateFontScheme(theme.fontScheme);
        } else {
            newTheme.themeElements.fontScheme = {
                name: 'Office',
                majorFont: {
                    cs: { typeface: '' },
                    latin: { typeface: this.ctx.defaultFont },
                    ea: { typeface: this.ctx.defaultFont },
                },
                minorFont: {
                    cs: { typeface: '' },
                    latin: { typeface: this.ctx.defaultFont },
                    ea: { typeface: this.ctx.defaultFont },
                },
            };
        }
        if (!isNil(theme.fmtScheme)) {
            newTheme.themeElements.fmtScheme = this.migrateFmtScheme(theme.fmtScheme);
        }
        if (!isNil(theme.themeElements.clrScheme)) {
            newTheme.themeElements.clrScheme = this.migrateColorScheme(theme.themeElements.clrScheme);
        }
        return newTheme;
    };

    public migrateFontScheme = (fontScheme) => {
        const newFontScheme: any = {};
        if (!isNil(fontScheme.majorFont)) {
            newFontScheme.majorFont = this.migrateFontCollection(fontScheme.majorFont);
        }
        if (!isNil(fontScheme.minorFont)) {
            newFontScheme.minorFont = this.migrateFontCollection(fontScheme.minorFont);
        }
        if (!newFontScheme.name) {
            newFontScheme.name = 'Office';
        }
        return newFontScheme;
    };

    public migrateFontCollection = (font) => {
        const newFontCollection: any = {
            ea: { typeface: '' },
            cs: { typeface: '' },
        };
        if (!isNil(font.latin)) {
            newFontCollection.latin = this.migrateFont({ typeface: font.latin });
        }
        if (!isNil(font.ea)) {
            newFontCollection.ea = this.migrateFont({ typeface: font.ea });
        }
        if (!isNil(font.Hans)) {
            newFontCollection.font = [this.migrateSupplementalHansFont(font)];
        }

        return newFontCollection;
    };

    public migrateFmtScheme = (fmtScheme) => {
        const newFmtScheme: any = {
            fillStyleLst: {},
            lnStyleLst: {},
            bgFillStyleLst: ThemeData.themeElements.fmtScheme.bgFillStyleLst,
            effectStyleLst: ThemeData.themeElements.fmtScheme.effectStyleLst,
        };
        if (!isNil(fmtScheme.fillStyleLst)) {
            newFmtScheme.fillStyleLst.egFillProperties = fmtScheme.fillStyleLst.map((i) => this.migrateBackground(i));
        }
        if (!isNil(fmtScheme.lnStyleLst)) {
            newFmtScheme.lnStyleLst.ln = fmtScheme.lnStyleLst.map((i) => this.migrateOutline(i).ln);
        }

        if (!newFmtScheme.name) {
            newFmtScheme.name = 'Office';
        }

        return newFmtScheme;
    };

    public migrateColorScheme = (clrScheme) => {
        if (!clrScheme) {
            return;
        }

        const newClrScheme: any = {};
        for (const key of Object.keys(clrScheme)) {
            newClrScheme[key] = {
                srgbClr: {
                    val: clrScheme[key],
                },
            };
        }

        if (!newClrScheme.name) {
            newClrScheme.name = 'Office';
        }

        return newClrScheme;
    };

    public migrateTextBody = (textBody?: any) => {
        if (!textBody) {
            return;
        }

        const txBody: any = {};

        // <xsd:element typeRedirected=dmlmain:CT_TextBodyProperties maxOccurs="1" minOccurs="1" name="bodyPr" type="CT_TextBodyProperties">
        txBody.bodyPr = this.migrateTextBodyProps(textBody.props) || {};

        if (textBody.textStyleList) {
            txBody.lstStyle = this.migrateTextStyleList(textBody.textStyleList);
        }

        // <xsd:element typeRedirected=dmlmain:CT_TextParagraph maxOccurs="unbounded" minOccurs="1" name="p" type="CT_TextParagraph">
        txBody.p = textBody.paragraphList?.map?.(this.migrateParagraph) || [];

        return txBody;
    };

    public migrateTextBodyProps = (props) => {
        if (!props) {
            return;
        }

        const bodyPr: any = {};

        if (!isNil(props.vert)) {
            bodyPr.vert = STTextVerticalType[props.vert];
        }

        if (!isNil(props.anchor)) {
            bodyPr.anchor = STTextAnchoringType[props.anchor];
        }

        switch (props.autoFitType) {
            case 'noAutofit':
                bodyPr.noAutofit = true;
                break;

            case 'normAutofit':
                bodyPr.normAutofit = {
                    fontScale: props.fontScale,
                    lnSpcReduction: props.lnSpcReduction,
                };
                break;

            case 'spAutoFit':
                bodyPr.spAutoFit = true;
                break;
        }

        if (!isNil(props.anchorCtr)) {
            bodyPr.anchorCtr = Boolean(props.anchorCtr);
        }

        if (!isNil(props.left_inset)) {
            bodyPr.lIns = props.left_inset;
        }

        if (!isNil(props.right_inset)) {
            bodyPr.rIns = props.right_inset;
        }

        if (!isNil(props.top_inset)) {
            bodyPr.tIns = props.top_inset;
        }

        if (!isNil(props.bottom_inset)) {
            bodyPr.bIns = props.bottom_inset;
        }

        // TODO
        if (!isNil(props.wrap)) {
            bodyPr.wrap = STTextWrappingType[props.wrap];
        }

        if (!isNil(props.numCol)) {
            bodyPr.numCol = props.numCol;
        }

        if (!isNil(props.spcCol)) {
            bodyPr.spcCol = props.spcCol;
        }

        return bodyPr;
    };

    public migrateTextStyleList = (textStyleList) => {
        if (!Array.isArray(textStyleList)) {
            return;
        }

        const lstStyle: any = {};
        const lstStyleMap = new Map<any, any>();

        for (const textStyle of textStyleList) {
            lstStyleMap.set(textStyle.level, this.migrateParagraphProps(textStyle));
        }

        Object.assign(lstStyle, {
            defPPr: lstStyleMap.get(-1),
            lvl1pPr: lstStyleMap.get(0),
            lvl2pPr: lstStyleMap.get(1),
            lvl3pPr: lstStyleMap.get(2),
            lvl4pPr: lstStyleMap.get(3),
            lvl5pPr: lstStyleMap.get(4),
            lvl6pPr: lstStyleMap.get(5),
            lvl7pPr: lstStyleMap.get(6),
            lvl8pPr: lstStyleMap.get(7),
            lvl9pPr: lstStyleMap.get(8),
        });

        return lstStyle;
    };

    public migrateParagraph = (paragraph) => {
        if (!paragraph) {
            return;
        }

        const pr: any = {};

        // 默认添加 pPr 属性
        pr.pPr = this.migrateParagraphProps(paragraph.paragraphProps);

        if (paragraph.paragraphItem) {
            pr.egTextRun = paragraph.paragraphItem.map(this.migrateParagraphItem);
        }

        // 默认添加 rPr 属性
        pr.endParaRPr = this.migrateRunProps(paragraph.endParaRPr);

        return pr;
    };

    public migrateParagraphItem = (paragraphItem) => {
        if (!paragraphItem) {
            return;
        }

        const textRun: any = {};

        switch (paragraphItem.type) {
            case 'run':
                textRun.r = {
                    rPr: this.migrateRunProps(paragraphItem.props),
                    t: paragraphItem.text,
                };

                break;

            case 'runBr':
                textRun.br = {
                    rPr: this.migrateRunProps(paragraphItem.props),
                };

                break;

            case 'field':
                textRun.fld = {
                    rPr: this.migrateRunProps(paragraphItem.props),
                    pPr: {},
                    t: paragraphItem.text,
                    type: paragraphItem.fieldType,
                    id: paragraphItem.id || `{${generateUpperUUID()}}`,
                };

                break;

            // 补充规则：默认解析为 run
            default:
                textRun.r = {
                    rPr: this.migrateRunProps(paragraphItem.props),
                    t: paragraphItem.text,
                };
        }

        return textRun;
    };

    public migrateParagraphProps = (paragraphProps) => {
        if (!paragraphProps) {
            return {};
        }

        const pPr: any = {};

        if (!isNil(paragraphProps.alignment)) {
            pPr.algn = STTextAlignType[paragraphProps.alignment];
        }

        if (!isNil(paragraphProps.character_bullet_char)) {
            pPr.buChar = {
                charKeyword: paragraphProps.character_bullet_char,
            };
        }

        if (!isNil(paragraphProps.buAutoNum)) {
            pPr.buAutoNum = {
                type: STTextAutonumberScheme[paragraphProps.buAutoNum.type],
                startAt: paragraphProps.buAutoNum.startAt,
            };
        }

        if (!isNil(paragraphProps.buNone)) {
            pPr.buNone = Boolean(paragraphProps.buNone);
        }

        if (!isNil(paragraphProps.buBlip?.blip)) {
            pPr.buBlip = {
                blip: this.migratePictureBlip(paragraphProps.buBlip.blip, undefined),
            };
        }

        if (!isNil(paragraphProps.buClr?.color)) {
            pPr.buClr = this.migrateColor(paragraphProps.buClr.color);
        }

        if (!isNil(paragraphProps.buClrTx)) {
            pPr.buClrTx = Boolean(paragraphProps.buClrTx);
        }

        if (!isNil(paragraphProps.buSzTx)) {
            pPr.buSzTx = Boolean(paragraphProps.buSzTx);
        }

        if (!isNil(paragraphProps.buFont)) {
            pPr.buFont = this.migrateFont(paragraphProps.buFont);
        }

        if (!isNil(paragraphProps.buFontTx)) {
            pPr.buFontTx = Boolean(paragraphProps.buFontTx);
        }

        if (!isNil(paragraphProps.buSzPct)) {
            pPr.buSzPct = { val: String(paragraphProps.buSzPct) };
        }

        if (!isNil(paragraphProps.buSzPts)) {
            pPr.buSzPts = { val: paragraphProps.buSzPts };
        }

        if (!isNil(paragraphProps.defTablSz)) {
            pPr.defTabSz = paragraphProps.defTablSz;
        }

        if (!isNil(paragraphProps.tabList)) {
            pPr.tabLst = {
                tab: paragraphProps.tabList.map(({ algn, pos }) => ({
                    pos,
                    algn: algn && STTextTabAlignType[algn],
                })),
            };
        }

        if (!isNil(paragraphProps.level)) {
            pPr.lvl = paragraphProps.level;
        }

        if (!isNil(paragraphProps.marginLeft)) {
            pPr.marL = paragraphProps.marginLeft;
        }

        if (!isNil(paragraphProps.marginRight)) {
            pPr.marR = paragraphProps.marginRight;
        }

        if (!isNil(paragraphProps.indent)) {
            pPr.indent = paragraphProps.indent;
        }

        if (!isNil(paragraphProps.defaultRunProps)) {
            pPr.defRPr = this.migrateRunProps(paragraphProps.defaultRunProps);
        }

        if (!isNil(paragraphProps.lnSpc)) {
            pPr.lnSpc = this.migrateTextSpacing(paragraphProps.lnSpc);
        }

        if (!isNil(paragraphProps.spcBef)) {
            pPr.spcBef = this.migrateTextSpacing(paragraphProps.spcBef);
        }

        if (!isNil(paragraphProps.spcAft)) {
            pPr.spcAft = this.migrateTextSpacing(paragraphProps.spcAft);
        }

        return pPr;
    };

    public migrateTextSpacing = (oldSpacing) => {
        const { spcPct, spcPts } = oldSpacing;
        // TextSpacing 不可以为 {}
        if (isNil(spcPct) && isNil(spcPts)) {
            return undefined;
        }
        const newSpacing: any = {};

        if (!isNil(spcPct)) {
            newSpacing.spcPct = { val: Math.min(13200000, Math.max(0, spcPct)) };
        }
        if (!isNil(spcPts)) {
            newSpacing.spcPts = { val: spcPts };
        }

        return newSpacing;
    };

    public migrateRunProps = (runProps) => {
        if (!runProps) {
            return {};
        }

        const rPr: any = {};

        if (!isNil(runProps.font)) {
            Object.assign(rPr, {
                latin: this.migrateFont(runProps.font.latin),
                ea: this.migrateFont(runProps.font.ea),
                cs: this.migrateFont(runProps.font.cs),
                sym: this.migrateFont(runProps.font.sym),
            });
        }

        if (!isNil(runProps.font_size)) {
            rPr.sz = runProps.font_size;
        }

        Object.assign(rPr, this.migrateBackground(runProps));
        Object.assign(rPr, this.migrateOutline(runProps.outline));

        if (!isNil(runProps.underline)) {
            rPr.u = STTextUnderlineType[runProps.underline];
        }

        if (!isNil(runProps.strike)) {
            rPr.strike = STTextStrikeType[runProps.strike];
        }

        if (!isNil(runProps.bold)) {
            rPr.b = Boolean(runProps.bold);
        }

        if (!isNil(runProps.italic)) {
            rPr.i = Boolean(runProps.italic);
        }

        if (!isNil(runProps.kern)) {
            rPr.kern = runProps.kern;
        }

        if (!isNil(runProps.spc)) {
            rPr.spc = runProps.spc;
        }

        if (!isNil(runProps.baseline)) {
            rPr.baseline = runProps.baseline;
        }

        if (!isNil(runProps.lang)) {
            rPr.lang = runProps.lang;
        }

        if (!isNil(runProps.highlight?.color)) {
            rPr.highlight = this.migrateColor(runProps.highlight.color);
        }

        if (!isNil(runProps.hlink)) {
            rPr.hlinkClick = this.migrateHLink(runProps.hlink);
        }

        if (!isNil(runProps.effectList)) {
            rPr.effectLst = this.migrateEffectLst(runProps.effectList);
        }

        return rPr;
    };

    public migrateFont = (font) => {
        if (!font) {
            return;
        }

        const textFont: any = {};

        if (!isNil(font.typeface)) {
            textFont.typeface = font.typeface;
        }

        if (!isNil(font.charset)) {
            textFont.charset = Number(font.charset);
        }

        return textFont;
    };

    // sdk2.0 只有 script 为 Hans 的字体，故只解析 Hans
    public migrateSupplementalHansFont = (font) => {
        const newFont: any = {};

        if (!isNil(font.Hans)) {
            newFont.script = 'Hans';
            newFont.typeface = font.Hans;
        }
        return newFont;
    };

    public migrateSection = (section) => {
        if (!this.ctx.section?.currentSlideIndex) {
            this.ctx.section = { currentSlideIndex: 0 };
        }
        const { currentSlideIndex } = this.ctx.section;

        const newSection: any = {
            type: DataType.Section,
            id: this.getId(section.id, 'section'),
            name: section.name,
            numSlides: section.numSlides,
        };

        if (currentSlideIndex < this.ctx.presentation.slideList.length) {
            newSection.firstSlideId = this.getPageId(this.ctx.presentation.slideList[currentSlideIndex]);
        }

        this.ctx.section.currentSlideIndex += section.numSlides ?? 0;
        return newSection;
    };

    public migrateResourceMap = (resource) => {
        const newResourceMap = {
            images: {},
            videos: {},
            audios: {},
            fonts: {},
        } as any;

        const { imageResource = {}, videoResource = {}, audioResource = {} } = resource ?? {};

        for (const [id, { src }] of Object.entries(imageResource.resourceMap ?? {}) as any) {
            const newImage = this.migrateImage(id, src);
            if (id) {
                newResourceMap.images[id] = newImage;
            }
        }

        for (const [id, { src, size, format, isExternal }] of Object.entries(videoResource.resourceMap ?? {}) as any) {
            if (id) {
                newResourceMap.videos[id] = {
                    id,
                    src,
                    size,
                    format,
                    isExternal,
                };
            }
        }

        for (const [id, { src, size, format, isExternal }] of Object.entries(audioResource.resourceMap ?? {}) as any) {
            if (id) {
                newResourceMap.audios[id] = {
                    id,
                    src,
                    size,
                    format,
                    isExternal,
                };
            }
        }

        return newResourceMap;
    };

    public getImgUrlFromBlipSrc = (blipSrc: string) => {
        const resourceMap = this.ctx.presentation?.resource?.imageResource?.resourceMap ?? {};
        if (blipSrc.startsWith('rel:')) {
            return resourceMap[blipSrc]?.src;
        }
        return blipSrc;
    };

    public getPrstGeomAdjVal = (adjOriginVal) => {
        if (adjOriginVal?.startsWith('val') && adjOriginVal.indexOf('.') > 0) {
            const val = adjOriginVal.slice(3).trim();
            return `val ${Math.floor(Number(val))}`;
        }
        return adjOriginVal;
    };

    public getPrstGeomAdjName = (adjLstLength, index, prstType, adjNameList) => {
        if (Object.keys(prstGeomGdNames).includes(prstType)) {
            const names = prstGeomGdNames[prstType];
            if (index <= names.length) {
                return names[index];
            }
        }
        if (Array.isArray(adjNameList) && index < adjNameList.length) {
            return adjNameList[index];
        }
        if (adjLstLength === 1 && !prstType.includes('Connector')) {
            return 'adj';
        }
        return `adj${index + 1}`;
    };

    public getPageId({ id, type }, createNewIfExist = false) {
        return this.getId([type, id].join(this.idSep), type, createNewIfExist);
    }

    public createShapeId(id) {
        return this.getShapeId(id, true);
    }

    public getShapeId(id, createNewIfExist = false) {
        if (!id) {
            return this.createShapeId(RandomIDGenerator.next());
        }

        return this.getId([this.ctx.page?.type, this.ctx.page?.id, id].join(this.idSep), 'shape', createNewIfExist);
    }

    public getNoteSlideId(slideData) {
        if (!slideData?.noteData) return;

        return this.getId(this.getPreNoteId(slideData), 'note');
    }

    public isNoteSlideIdExisted(slideData) {
        return !!this.ctx.idsMap[this.getPreNoteId(slideData)];
    }

    public getPreNoteId(slideData) {
        return [slideData.type, 'note', slideData.id].join(this.idSep);
    }

    public getId(id, type, createNewIfExist = false) {
        if (!id) {
            return this.getId(RandomIDGenerator.next(), type);
        }

        if (!this.ctx.idsMap[id]) {
            if (this.useRandomId) {
                this.ctx.idsMap[id] = RandomIncrementIDGenerator.next(8);
            } else {
                const modelId = [type, String(this.idGenerator.next()).padStart(8, '0')].join('_');
                this.ctx.idsMap[id] = [this.ctx.sessionId, modelId].filter(Boolean).join(this.idSep);
            }
        } else if (createNewIfExist) {
            return this.getId(RandomIDGenerator.next(), type);
        }

        return this.ctx.idsMap[id];
    }

    public getExtname(path: string) {
        const index = path.lastIndexOf('.');
        return index >= 0 ? path.slice(index) : '';
    }

    private getNumberWithMinPrecision(num: number) {
        return Number(num.toFixed(0));
    }

    private migrateImage = (id: string, src: string) => {
        const url = new URL(src);
        return {
            id,
            type: IMAGE_MIME_TYPE.get(this.getExtname(url.pathname)) ?? '',
            src,
            width: parseInt(url.searchParams.get('w') ?? '', 10) || 128,
            height: parseInt(url.searchParams.get('h') ?? '', 10) || 128,
        };
    };

    private addImageResource = (src: string, type?: string) => {
        if (!(typeof src === 'string')) return;
        if (src.startsWith('https://')) {
            const id = `rel:${getHashStringWith64Bytes(src)}`;
            if (!this.hasResource(id)) {
                set(this.ctx.presentation, `resource.imageResource.resourceMap[${id}]`, { id, src, type });
            }
            return id;
        }
        return src;
    };

    private hasResource = (id: string) => {
        const { images, videos, audios, fonts } = this.ctx.presentation?.resource?.imageResource?.resourceMap ?? {};
        return !!(images?.[id] || videos?.[id] || audios?.[id] || fonts?.[id]);
    };

    private fromRgbHexString(hexStr: string): string {
        function hex(val: number): string {
            return val.toString(16).padStart(2, '0').toUpperCase();
        }
        const v = parseInt(hexStr.toLowerCase().replace(/[^0-9a-f]/g, ''), 16);
        const r = (0xff0000 & v) >> 16;
        const g = (0x00ff00 & v) >> 8;
        const b = 0x0000ff & v;
        return `${hex(r)}${hex(g)}${hex(b)}`;
    }

    // 脏数据兼容，layoutId 不存在的 slide 随便放一个已知的 layoutId。保证页面可以显示即可
    private resetLayoutId(newData: any) {
        newData.slideList.forEach((slide) => {
            if (!newData.layoutMap[slide.layoutId]) {
                slide.layoutId = Object.keys(newData.layoutMap)[0];
            }
        });
    }
}
